/**
 * English is the source of truth: every other locale is typed against this
 * object, so a missing or misspelled key is a compile error rather than a raw
 * `some.key.name` rendered on screen.
 *
 * Nested and namespaced, unlike the old flat file of 2,469 keys where
 * `orders`, `store` and `help` each had to serve several unrelated screens.
 */
export const en = {
  common: {
    appName: 'Grocefy Admin',
    email: 'Email',
    password: 'Password',
    signIn: 'Sign in',
    signOut: 'Sign out',
    cancel: 'Cancel',
    save: 'Save',
    search: 'Search',
    noOptionsMatch: 'No options match.',
    typeToFilter: 'Type to filter…',
    noStoreSelected: 'Choose a store from the switcher to see its settings.',
    loading: 'Loading…',
    back: 'Back',
    backToDashboard: 'Back to dashboard',
    copy: 'Copy',
    retry: 'Try again',
    reload: 'Reload',
    yes: 'Yes',
    no: 'No',
    notAvailable: 'N/A',
    language: 'Language',
    english: 'English',
    spanish: 'Español',
  },

  auth: {
    login: {
      title: 'Sign in to your account',
      subtitle: 'Enter the credentials for your admin account.',
      emailPlaceholder: 'you@company.com',
      forgotPassword: 'Forgot password?',
      submit: 'Sign in',
      submitting: 'Signing in…',
      needAccount: 'Need an account?',
      requestAccess: 'Request access',
      genericError: 'Sign in failed. Please try again.',
      showPassword: 'Show password',
      hidePassword: 'Hide password',
    },

    register: {
      title: 'Request an account',
      subtitle:
        'Create your credentials. An administrator assigns your role and stores before you can sign in.',
      firstName: 'First name',
      lastName: 'Last name',
      phone: 'Phone number',
      phonePlaceholder: '(787) 555-0123',
      gender: 'Gender',
      male: 'Male',
      female: 'Female',
      preferredLanguage: 'Preferred language',
      confirmPassword: 'Confirm password',
      submit: 'Request account',
      submitting: 'Creating account…',
      haveAccount: 'Already have an account?',
      genericError: 'Could not create the account. Please try again.',
      successTitle: 'Account requested',
      successBody:
        'Your request has been submitted. An administrator has to grant your account a role before you can sign in — you will be notified once that happens.',
      backToSignIn: 'Back to sign in',
      passwordRules: {
        length: 'At least 8 characters',
        uppercase: 'One uppercase letter',
        lowercase: 'One lowercase letter',
        number: 'One number',
      },
    },

    validation: {
      firstNameRequired: 'Enter your first name',
      lastNameRequired: 'Enter your last name',
      emailRequired: 'Enter your email',
      emailInvalid: 'Enter a valid email',
      phoneRequired: 'Enter your phone number',
      phoneInvalid: 'Enter a phone number with at least 10 digits',
      passwordRequired: 'Enter your password',
      passwordCreate: 'Enter a password',
      passwordWeak: 'Password does not meet the requirements below',
      confirmRequired: 'Confirm your password',
      passwordMismatch: 'Passwords do not match',
      notAuthorized: 'This account is not authorized to use the admin portal.',
    },

    brand: {
      eyebrow: 'Admin Console',
      headline: 'Everything your stores do, in one place.',
      subhead:
        'One console for the whole operation — no more switching between tools to answer a single question.',
      support: 'Trouble signing in? Contact your account administrator.',
      calloutWithBooking: 'Booking + retail',
      calloutWithoutBooking: 'Retail',
      calloutBodyWithBooking:
        'Reservations and retail sales report side by side, so a slow Tuesday shows up once — not twice in two different systems.',
      calloutBodyWithoutBooking:
        'Sales, stock and fulfilment report against the same numbers, so a slow Tuesday shows up once — not twice in two different systems.',
      orders: 'Orders',
      ordersBody: 'Every order from checkout to hand-off, with the history behind it.',
      bookings: 'Bookings',
      bookingsBody:
        'Events, hosts, timeslots and memberships across all your locations.',
      catalog: 'Catalog',
      catalogBody: 'Items, pricing and stock, kept in step across every store.',
      delivery: 'Delivery',
      deliveryBody: 'Dispatch, providers and routes on one board.',
      marketing: 'Marketing',
      marketingBody:
        'Campaigns, coupons and loyalty, measured against real sales.',
      finance: 'Finance',
      financeBody: 'Payouts, invoices and reporting you can reconcile.',
    },

    publicNotice: {
      forgotTitle: 'Reset your password',
      forgotBody: 'Password recovery is not part of the redesign yet.',
    },
  },

  shell: {
    openNavigation: 'Open navigation',
    navigation: 'Navigation',
    mainNavigation: 'Main',
    collapse: 'Collapse',
    collapseSidebar: 'Collapse sidebar',
    expandSidebar: 'Expand sidebar',
    searchPlaceholder: 'Search…',
    searchPages: 'Search pages',
    searchPagesShortcut: 'Search pages (⌘K)',
    commandPlaceholder: 'Search pages…',
    commandEmpty: 'No pages match that search.',
    commandIn: 'in {{parent}}',
    appearance: 'Appearance',
    themeLight: 'Light',
    themeDark: 'Dark',
    themeSystem: 'System',
    notImplemented: '{{name}} is not available yet.',
    profile: 'Profile',
    footerRights: '© {{year}} Gopanza LLC. All rights reserved.',
  },

  businessSwitcher: {
    switchContext: 'Switch context',
    global: 'Global',
    store: 'Store',
    viewing: 'Viewing',
    ariaLabel: 'Current context: {{context}}. Change store.',
    loadFailed: 'Could not load your stores.',
    noneAssigned: 'No stores assigned to this account.',
  },

  customers: {
    title: 'Users',
    subtitle:
      'Look up a customer by name, email, phone number or friend code.',
    searchPlaceholder: 'Search by name, email or phone…',
    searchLabel: 'Search customers',
    clearSearch: 'Clear search',

    startTitle: 'Search for a customer',
    startBody:
      'Enter a name, email address, phone number or friend code to begin. Nothing is listed until you search.',
    noResultsTitle: 'No customers found',
    noResultsBody: 'Nothing matched "{{term}}". Check the spelling, or try a phone number or email instead.',
    errorTitle: 'Search failed',
    errorBody: 'The customer search could not be completed. Please try again.',
    resultCount_one: '{{count}} customer',
    resultCount_other: '{{count}} customers',
    ordersCount_one: '{{count}} order',
    ordersCount_other: '{{count}} orders',

    exportMarketing: 'Export marketing users',
    exportShort: 'Export',
    exportFailed: 'The export could not be generated.',

    name: 'Name',
    email: 'Email',
    phone: 'Phone',
    orders: 'Orders',
    orderCount: 'Order count',
    status: 'Status',
    active: 'Active',
    inactive: 'Inactive',
    deleted: 'Deleted',
    verified: 'Verified',
    unverified: 'Unverified',
    createdAt: 'Created',

    details: 'Details',
    locations: 'Locations',
    cards: 'Cards',
    activity: 'Activity',
    friendCode: 'Friend code',
    userSince: 'User since',
    accountStatus: 'Account status',
    communicationPreferences: 'Communication preferences',
    allowPush: 'Push notifications',
    allowEmails: 'Emails',
    allowSms: 'SMS',

    locationName: 'Name',
    address: 'Address',
    city: 'City',
    zipcode: 'ZIP code',
    noLocations: 'This customer has no saved addresses.',

    nickname: 'Nickname',
    reference: 'Reference',
    type: 'Type',
    expires: 'Expires',
    noCards: 'This customer has no saved cards.',
    overview: 'Overview',
    registered: 'Registered',
    registeredToday: 'Registered today',
    atLeastOneOrder: 'At least one order',

    actions: 'Actions',
    editUser: 'Edit user',
    updated: 'Changes saved.',
    updateFailed: 'The change could not be saved.',
    actionFailed: 'That action could not be completed.',
    copiedToClipboard: 'Copied to clipboard',

    tabProfile: 'Profile',
    tabFlags: 'Settings',
    tabRoles: 'Roles',
    tabActions: 'Actions',

    externalClientId: 'External client ID',
    flagsHint: 'Each switch saves on its own, as soon as you change it.',
    rolesHint: 'Roles apply across the whole platform and take effect immediately.',
    roleUpdated: 'Role updated.',
    roleFailed: 'The role could not be updated.',

    isBlocked: 'Blocked',
    isGuest: 'Guest account',
    isHighRisk: 'High risk',
    canMakeOrders: 'Can place orders',
    betaStores: 'Can view beta stores',
    alphaStores: 'Can view alpha stores',
    betaInfoCards: 'Can view beta info cards',

    passwordSection: 'Password',
    resetPassword: 'Reset password',
    resetPasswordRandom: 'Reset to a random password',
    resetPasswordConfirmTitle: 'Reset this password?',
    resetPasswordConfirmBody:
      'A new random password is generated and copied to your clipboard. The current password stops working immediately.',
    newPassword: 'New password',

    codesSection: 'Access codes',
    sendLoginCode: 'Send login code',
    resetLoginCode: 'Reset login code',
    getManagerCode: 'Get manager code',
    generateManagerCode: 'Generate new manager code',

    dangerSection: 'Danger zone',
    deleteUser: 'Delete user',
    deleteConfirmTitle: 'Delete this account?',
    deleteConfirmBody:
      'This permanently deletes {{name}} and cannot be undone.',
    funding: {
      credit: 'Credit',
      debit: 'Debit',
      prepaid: 'Prepaid',
      unknown: 'Unknown',
    },
  },

  /**
   * Account requests — people asking for an account, before there is one.
   * Only shown for clients that take applications (`VITE_HAS_ACCOUNT_REQUESTS`).
   */
  accountRequests: {
    title: 'Account Requests',
    subtitle:
      'People who have applied for an account. Approve one to send them a sign-up link.',
    searchPlaceholder: 'Search by name, business, email or phone…',
    showing: 'Showing',
    all: 'All',
    view: 'View',

    resultCount_one: '{{count}} request',
    resultCount_other: '{{count}} requests',
    noneInQueue: 'Nothing here right now.',
    noResults: 'Nothing matched "{{term}}".',
    loadFailed: 'The account requests could not be loaded.',

    applicant: 'Applicant',
    email: 'Email',
    phone: 'Phone',
    requestedOn: 'Requested',
    statusColumn: 'Status',
    noName: 'No name given',

    status: {
      pending: 'Pending',
      approved: 'Approved',
      rejected: 'Rejected',
      registered: 'Registered',
    },

    requestDetails: 'Request details',
    linkExpiresOn: 'link valid until {{date}}',
    linkExpiredOn: 'link expired {{date}}',
    alreadyRegistered:
      'This applicant has already created their account, so the request can no longer be changed.',

    approve: 'Approve',
    approveConfirm:
      'A sign-up link is emailed to {{name}} and stays valid for a limited time.',
    approvedToast: 'Request approved.',

    reject: 'Reject',
    rejectConfirm: "The request from {{name}} is turned down and cannot be undone from here.",
    rejectionMessage: 'Message (optional)',
    rejectionPlaceholder: 'Sent to the applicant with the decision.',
    rejectedToast: 'Request rejected.',

    getApprovalLink: 'Copy sign-up link',
    linkCopied: 'Sign-up link copied to the clipboard.',

    saved: 'Changes saved.',
    actionFailed: 'That action could not be completed.',

    businessName: 'Business name',
    firstName: 'First name',
    lastName: 'Last name',
    gender: 'Gender',
    male: 'Male',
    female: 'Female',
    notSaid: 'Prefer not to say',
    addressLine1: 'Address line 1',
    addressLine2: 'Address line 2',
    city: 'City',
    state: 'State',
    country: 'Country',
    externalClientId: 'External client ID',
    businessLicense: 'Business licence',
    referralCode: 'Referral code',
  },

  /**
   * The page-visit timeline. Its own block rather than a corner of `customers`:
   * the panel is shown wherever one account is being looked at, and the
   * customer tab is only the first of those.
   */
  pageHistory: {
    help: 'Pages this account opened, most recent first, across every app it uses.',
    allApps: 'All apps',
    /**
     * Names for the apps that write visits. The API accepts any well-formed
     * domain without being told about it first, so an app with no name here is
     * shown by its slug rather than left out.
     */
    apps: {
      'admin-portal': 'Admin portal',
      'webfront': 'Webfront'
    },
    lastDays_one: 'Last day',
    lastDays_other: 'Last {{count}} days',
    retained: 'Everything kept',
    today: 'Today',
    yesterday: 'Yesterday',
    global: 'Global',
    none: 'No pages recorded for this account in this period.',
    noneForApp: 'No pages recorded in this app for this account in this period.',
    failed: 'The page history could not be loaded.',
    loadMore: 'Load more',
  },

  /**
   * The app usage report. Reads the same visits as `pageHistory`, across
   * accounts instead of for one, so the app names are shared with it.
   */
  usage: {
    title: 'App usage',
    subtitle:
      'Which pages people open and when, for one person or a group beside another.',
    retention: 'Visits are kept for 180 days.',
    loadFailed: 'The report could not be loaded.',
    rangeInvalid: 'The start date is after the end date.',
    noSubjects: 'Add a person or a group to see how they use the apps.',
    noVisits: 'No visits in this period.',
    noPages: 'No pages were opened in this period.',

    from: 'From',
    to: 'To',
    app: 'App',

    subjects: 'Comparing',
    subjectsHelp:
      'Each person or group is shown on its own. They are compared, never added together, so they may overlap.',
    subjectsFull_one: 'One is the most that can be compared at once.',
    subjectsFull_other:
      '{{count}} is the most that can be compared at once. Remove one to add another.',
    addSubject: 'Add',
    addSubjectHelp: 'One account, or everybody matching a set of filters.',
    removeSubject: 'Remove {{name}}',
    person: 'Person',
    group: 'Group',
    everyone: 'Everyone',
    unnamed: 'Unnamed account',

    personPlaceholder: 'Search by email, name or phone',
    personHint: 'Type at least three characters.',
    personNone: 'No accounts match.',
    personFailed: 'The search could not be run.',
    alreadyAdded: 'Added',

    any: 'Any',
    gender: 'Gender',
    male: 'Male',
    female: 'Female',
    genderNotSaid: 'Gender not said',
    ageGroup: 'Age group',
    ageCaveat:
      'Accounts with no birth date on file are left out of an age group.',
    accountType: 'Account type',
    registered: 'Registered',
    guests: 'Guests',
    addGroup: 'Add group',
    addEveryone: 'Add everyone',

    summaryTitle: 'Summary',
    summaryHelp:
      'The change beside visits is against the same number of days just before this period.',
    subject: 'Who',
    members: 'Accounts',
    activeUsers: 'Active users',
    visits: 'Visits',
    sessions: 'Sessions',
    visitsPerUser: 'Visits per active user',
    pagesPerSession: 'Pages per session',
    lastSeen: 'Last seen',
    viewMembers: 'View accounts',
    openCustomer: 'Open customer',

    trendTitle: 'Trend',
    trendMetric: 'What to plot',
    trendHelp: {
      perUser:
        'Visits on each day divided by the accounts active that day, so one person and a large group share a scale.',
      activeUsers: 'Accounts with at least one visit on each day.',
      visits: 'Pages opened on each day.',
    },

    pagesTitle: 'Top pages',
    pagesHelp:
      "Each figure is that page's share of the subject's own visits. Bars are drawn to one scale across the table.",
    page: 'Page',
    pageCell: '{{visits}} visits by {{users}} accounts',
    showAllPages: 'Show all {{count}} pages',
    showFewer: 'Show fewer',

    heatTitle: 'When',
    heatHelp:
      'Visits by day of the week and hour, in your own time zone. Each grid is shaded against its own busiest hour.',
    heatPeak_one: 'Busiest hour: {{formatted}} visit',
    heatPeak_other: 'Busiest hour: {{formatted}} visits',

    favoriteStoreIs: 'Favourite: {{store}}',
    joinedFrom: 'Joined on or after',
    joinedTo: 'Joined on or before',
    joinedBetween: 'Joined {{from}} – {{to}}',
    joinedSince: 'Joined since {{date}}',
    joinedBefore: 'Joined by {{date}}',

    distinctTitle: 'What sets them apart',
    distinctHelp:
      "Pages each subject opens more, or less, than everyone does. The multiple compares the page's share of their visits with its share of everyone's, shown beside it.",
    usesMore: 'More than everyone',
    usesLess: 'Less than everyone',
    never: 'Never',
    nothingStandsOut: 'Nothing stands out.',
    tooFewVisits_one: 'Fewer than {{count}} visit in this period, which is too few to compare.',
    tooFewVisits_other:
      'Fewer than {{count}} visits in this period, which is too few to compare.',

    moversTitle: 'Rising and falling',
    moversHelp:
      "Pages whose share of each subject's visits changed most against the same number of days just before. Figures are percentage points.",
    rising: 'Rising',
    falling: 'Falling',
    nothingMoved: 'Nothing moved.',
    noEarlierPeriod:
      'The period before this one is no longer kept, so there is nothing to compare with.',
    tooFewVisitsBoth_one:
      'Fewer than {{count}} visit in this period or the one before, which is too few to compare.',
    tooFewVisitsBoth_other:
      'Fewer than {{count}} visits in this period or the one before, which is too few to compare.',

    breakdownTitle: 'Breakdown',
    breakdownHelp:
      "Each subject's visits split one way. Figures are shares of that subject's own visits.",
    breakdownBy: 'Split by',
    dimensions: {
      age: 'Age group',
      gender: 'Gender',
      language: 'Language',
      domain: 'App',
      business: 'Store',
    },
    ageUnknown: 'No birth date',
    ageUnder18: 'Under 18',
    otherLanguage: 'Other',

    retentionTitle: 'Who came back',
    retentionHelp:
      'By week, Sunday to Saturday. New means first seen in the visits still kept, not a new account, so the earliest weeks on record are mostly new.',
    returning: 'Returning',
    newAccounts: 'New',
    tallestBar: 'Tallest bar: {{formatted}} accounts',
    weekOf: 'Week of {{date}}',
    cohortTitle: 'Of the accounts first seen each week, the share active in the weeks after',
    firstSeen: 'First seen, week of',
    cohortCell: '{{users}} of {{size}} accounts',
    lapsedCount_one: '{{formatted}} account was active just before this period and not in it.',
    lapsedCount_other:
      '{{formatted}} accounts were active just before this period and not in it.',
    viewLapsed: 'View',
    lapsedHelp:
      'Accounts in this group that visited in the same number of days just before this period and have not since. Visits and dates are from that earlier period.',
    noLapsed: 'Nobody lapsed.',

    exportCsv: 'Export CSV',
    exportFailed: 'The export could not be made.',
    exportCapped_one: 'The export stops at {{formatted}} account. There are more.',
    exportCapped_other: 'The export stops at {{formatted}} accounts. There are more.',

    previousVisits: 'Visits in the period before',
    lastSeenUtc: 'Last seen (UTC)',
    pageKey: 'Page key',
    share: 'Share',

    flowsTitle: 'Flows',
    flowsHelp:
      'How sessions move through the apps. A session is one browser tab, not one sign-in, and only the part of it inside this period is counted.',
    flowEntries: 'Starts on',
    flowExits: 'Ends on',
    flowNext: 'What comes next',
    flowDepth: 'Session length',
    flowDepthHeading: 'Pages in the session',
    flowAfter: 'After opening',
    flowCell: '{{part}} of {{total}} sessions',
    nextCell: '{{part}} of {{total}} visits to the page',
    sessionEnded: 'Nothing: the session ended',
    noSessions: 'No sessions were recorded in this period.',
    noNext: 'Nobody opened this page in this period.',
    sessionCount_one: '{{formatted}} session',
    sessionCount_other: '{{formatted}} sessions',
    fromVisitCount_one: '{{formatted}} visit to the page',
    fromVisitCount_other: '{{formatted}} visits to the page',
    depth: {
      one: '1 page',
      range: '{{range}} pages',
      many: '11 or more pages',
    },

    unusedTitle: 'Rarely opened portal pages',
    unusedHelp:
      'Admin portal pages that everyone together opened fewer than five times in this period, whoever is being compared above. Read it over a long period: a page used once a quarter is quiet in any one month.',
    neverOpened: 'Never opened',
    noUnused_one: 'Every page was opened at least {{count}} time.',
    noUnused_other: 'Every page was opened at least {{count}} times.',

    savedViews: 'Saved views',
    savedViewsHelp:
      'Comparisons you have named, kept in this browser. A view saved without picking dates stays on the last 30 days.',
    saveCurrent: 'Save what is on screen as',
    viewNamePlaceholder: 'e.g. Women 25–34 vs everyone',
    savedViewsFull: 'The list is full. Saving another drops the oldest.',
    noSavedViews: 'Nothing saved yet.',
    deleteView: 'Delete {{name}}',

    membersHelp: 'Accounts in this group that visited in the period, busiest first.',
    visitCount_one: '{{formatted}} visit',
    visitCount_other: '{{formatted}} visits',
  },

  pagination: {
    showing: 'Showing {{first}}–{{last}} of {{total}}',
    pageOf: 'Page {{page}} of {{pages}}',
    pageSize: 'Rows per page',
    previous: 'Previous page',
    next: 'Next page',
  },

  orders: {
    title: 'Orders',
    subtitle: 'Every order across all stores, filtered by status and service window.',
    searchPlaceholder: 'Search by confirmation code, customer or phone...',
    searchLabel: 'Search orders',

    order: 'Order',
    typeColumn: 'Type',
    customer: 'Customer',
    total: 'Total',
    payment: 'Payment',
    statusColumn: 'Status',
    serviceWindow: 'Service window',
    documents: 'Documents',
    signature: 'Signature',
    ebtReceipt: 'EBT receipt',
    new: 'New',

    itemCount_one: '{{count}} item',
    itemCount_other: '{{count}} items',
    resultCount_one: '{{count}} order',
    resultCount_other: '{{count}} orders',
    totalRevenue: 'Revenue',

    noResultsTitle: 'No orders match',
    noResultsBody: 'Try a different status, a wider time range, or clear the filters.',
    errorBody: 'Orders could not be loaded. Please try again.',

    items: 'Items',
    history: 'History',
    item: 'Item',
    upc: 'UPC',
    quantity: 'Qty',
    price: 'Price',
    noItems: 'This order has no items.',
    noLogs: 'No history recorded for this order.',
    itemsFailed: 'The items could not be loaded.',
    logsFailed: 'The history could not be loaded.',

    sort: 'Sort',
    oldest: 'Oldest first',
    newest: 'Newest first',
    actions: 'Actions',
    logs: 'Logs',
    voided: 'Voided',

    general: 'General',
    detailsTab: 'Details',
    pickupName: 'Pickup name',
    dates: 'Dates',
    created: 'Created',
    expected: 'Expected',
    leadTime: 'Lead time',
    hours_one: '{{count}} hour',
    hours_other: '{{count}} hours',
    dispatched: 'Dispatched',
    method: 'Method',
    giftCard: 'Gift card',
    card: 'Card',
    cardHolder: 'Card holder',
    authorized: 'Authorized',
    charged: 'Charged',
    ivuControl: 'IVU control',
    totals: 'Totals',
    subtotal: 'Subtotal',
    municipalTax: 'Municipal tax',
    stateTax: 'State tax',
    serviceFee: 'Service fee',
    deliveryFee: 'Delivery',
    discount: 'Discount',
    return: 'Return',
    transfers: 'Transfers',
    transferPending: 'Pending',
    transferPaidOut: 'Paid out',
    transferPaidAmount: 'Paid amount',
    posLane: 'POS lane',
    terminalId: 'Terminal ID',
    version: 'Version',
    deviceName: 'Device name',
    deviceModel: 'Device model',
    deviceManufacturer: 'Manufacturer',
    delivery: 'Delivery',
    zone: 'Zone',
    driver: 'Driver',
    driverPhone: 'Driver phone',
    driverInstructions: 'Driver instructions',
    deliveryAddress: 'Delivery address',
    noAddress: 'No delivery address on this order.',

    fulfilment: 'Fulfilment',
    dispatch: 'Dispatch',
    dispatched2: 'Dispatched',
    markDelivered: 'Mark as delivered',
    changeToPickup: 'Change to pickup',
    changeStatus: 'Change status',
    selectStatus: 'Select a status',
    changePaymentMethod: 'Change payment method',
    selectPayment: 'Select a method',
    toggleServiceFee: 'Toggle service fee',
    forceCapture: 'Force capture',
    cancelOrder: 'Cancel order',
    cancelConfirmTitle: 'Cancel this order?',
    cancelConfirmBody: 'Order {{code}} will be cancelled. This cannot be undone from here.',
    actionFailed: 'That action could not be completed.',
    instructionsSaved: 'Driver instructions saved.',
    changedToPickup: 'Order changed to pickup.',
    statusChanged: 'Status updated.',
    paymentChanged: 'Payment method updated.',
    serviceFeeToggled: 'Service fee toggled.',
    captured: 'Payment captured.',
    cancelled: 'Order cancelled.',
    markedDelivered: 'Order marked as delivered.',
    moreActionsNote:
      'Assigning a driver and changing the card are not built yet.',

    receipt: 'Receipt',
    print: 'Print',
    close: 'Close',
    terminalData: 'Terminal data',
    service: 'Service',
    contact: 'Contact',
    date: 'Date',
    store: 'Store',
    location: 'Location',
    itemsIncluded: 'Items included ({{count}})',
    itemsNotIncluded: 'Items not included ({{count}})',
    paymentInformation: 'Payment information',
    serviceType: 'Service type',
    changeToDelivery: 'Change to delivery',
    changedToDelivery: 'Order changed to delivery.',
    chooseAddress: 'Choose the delivery address for this order.',
    addressesFailed: 'The addresses could not be loaded.',
    noDeliverableAddresses:
      'This customer has no address this store can deliver to.',

    paymentLogs: 'Payment logs',
    noPaymentLogs: 'No payment logs for this order.',
    createdOn: 'Created on',
    operation: 'Operation',
    preview: 'Preview',
    serviceCost: 'Service cost',
    giftCardBalance: 'Gift card balance: {{amount}}',
    giftCardFailed: 'The gift card balance could not be loaded.',

    stats: {
      pending: 'Placed',
      processing: 'Processing',
      waiting: 'Waiting',
      inTransit: 'In transit',
      completed: 'Completed',
    },

    filters: {
      any: 'Any',
      status: 'Status',
      time: 'Time',
      type: 'Type',
      payment: 'Payment',
      store: 'Store',
      allStores: 'All stores',
      reset: 'Clear filters',
      active: 'Active',
      processing: 'Processing',
      picking: 'Picking',
      packing: 'Packing',
      waiting: 'Waiting',
      inTransit: 'In transit',
      completed: 'Completed',
      cancelled: 'Cancelled',
      paymentFailed: 'Payment failed',
      pending: 'Pending',
      past: 'Past',
      yesterday: 'Yesterday',
      today: 'Today',
      tomorrow: 'Tomorrow',
      future: 'Future',
      creditCard: 'Credit card',
    },

    type: {
      pickup: 'Pickup',
      delivery: 'Delivery',
      inStore: 'In store',
      deliPickup: 'Deli pickup',
      deliDelivery: 'Deli delivery',
      unknown: 'Unknown',
    },

    status: {
      placed: 'Placed',
      deliveryAssigned: 'Driver assigned',
      accepted: 'Accepted',
      ready: 'Ready',
      deliveryStarted: 'Out for delivery',
      customerPickedUp: 'Picked up',
      delivered: 'Delivered',
      storeCancelled: 'Cancelled by store',
      userCancelled: 'Cancelled by customer',
      declined: 'Declined',
      pickingStarted: 'Picking',
      pickingFinished: 'Picked',
      packingStarted: 'Packing',
      readyForPickup: 'Ready for pickup',
      pendingDeliveryAssignment: 'Awaiting driver',
      pendingPaypalPayment: 'Awaiting PayPal',
      pendingPayment: 'Awaiting payment',
      underReview: 'Under review',
      ivuControlPending: 'IVU control pending',
      unknown: 'Unknown',
    },
  },

  memberships: {
    title: 'Membership Plans',
    subtitle: 'The plans customers can buy, and the benefits each one grants.',
    searchPlaceholder: 'Search by name or location...',
    showingOf: 'Showing {{shown}} of {{total}}',
    createNew: 'Create new',
    noPlans: 'No membership plans have been created yet.',
    noMatches: 'No plans match these filters.',
    loadFailed: 'The membership plans could not be loaded.',

    location: 'Location',
    name: 'Name',
    description: 'Description',
    internalName: 'Internal name',
    isActive: 'Active',
    active: 'Active',
    inactive: 'Inactive',
    any: 'Any',
    recurrence: 'Recurrence',
    price: 'Price',
    isOnPromo: 'On promo',
    actions: 'Actions',
    edit: 'Edit',
    benefits: 'Benefits',
    ordering: 'Ordering',
    includedMembers: 'Included members',
    image: 'Image',
    clickOrDrag: 'Choose an image',
    uploadFailed: 'The image could not be uploaded.',
    update: 'Update',
    create: 'Create',
    saveFailed: 'The changes could not be saved.',
    deleteFailed: 'That could not be deleted.',

    editPlan: 'Edit membership plan',
    createPlan: 'Create membership plan',
    planSaved: 'Membership plan saved.',
    deletePlan: 'Delete plan',
    planDeleted: 'Membership plan deleted.',
    deletePlanConfirmTitle: 'Delete this plan?',
    deletePlanConfirmBody:
      'The plan and its benefits are removed. This cannot be undone.',

    allowRenewal: 'Allow renewal',
    allowPaypalSubscription: 'Allow PayPal subscription',
    hasLimit: 'Has limit',
    limit: 'Limit',
    hasPromo: 'Has promo',
    promoStart: 'Promo starts',
    promoEnd: 'Promo ends',
    promoPrice: 'Promo price',
    dateExtensionType: 'Date extension type',
    dateExtension: 'Date extension',
    hasAutoRenewalDiscount: 'Has auto-renewal discount',
    autoRenewalDiscountType: 'Discount type',
    autoRenewalDiscountValue: 'Discount value',

    addBenefit: 'Add benefit',
    editBenefit: 'Edit benefit',
    benefitSaved: 'Benefit saved.',
    deleteBenefit: 'Delete benefit',
    benefitDeleted: 'Benefit deleted.',
    deleteBenefitConfirmTitle: 'Delete this benefit?',
    deleteBenefitConfirmBody: 'This cannot be undone.',
    noBenefits: 'This plan has no benefits yet.',
    benefitsFailed: 'The benefits could not be loaded.',
    isPromoBenefit: 'Promo benefit',
    applicableEventType: 'Applicable event type',
    applicableServiceType: 'Applicable service type',
    type: 'Type',
    amount: 'Amount',

    recurrenceOptions: {
      none: 'None',
      daily: 'Daily',
      weekly: 'Weekly',
      biweekly: 'Biweekly',
      monthly: 'Monthly',
      bimonthly: 'Bimonthly',
      quarterly: 'Quarterly',
      semiyearly: 'Semiyearly',
      yearly: 'Yearly',
    },

    discountType: {
      fixed: 'Fixed',
      percentage: 'Percentage',
    },

    extension: {
      none: 'None',
      days: 'Days',
      weeks: 'Weeks',
      months: 'Months',
      years: 'Years',
    },

    eventType: {
      rental: 'Rental',
      class: 'Class',
      other: 'Other',
      yoga: 'Yoga',
      service: 'Service',
      none: 'None',
    },

    benefitType: {
      fixedDiscount: 'Fixed discount',
      percentDiscount: 'Percent discount',
      specialPrice: 'Special price',
      free: 'Free',
      earlyVisibility: 'Early visibility',
      posFixedDiscount: 'POS fixed discount',
      posPercentDiscount: 'POS percent discount',
    },
  },

  events: {
    title: 'Events',
    subtitle: 'The events customers can book a time slot in.',
    searchPlaceholder: 'Search by name, location or host...',
    showingCount_one: 'Showing {{count}} {{state}} event',
    showingCount_other: 'Showing {{count}} {{state}} events',
    createNew: 'Create new',
    noEvents: 'No events have been created yet.',
    noMatches: 'No events match these filters.',
    loadFailed: 'The events could not be loaded.',

    all: 'All',
    any: 'Any',
    active: 'Active',
    inactive: 'Inactive',
    isActive: 'Active',
    name: 'Name',
    internalName: 'Internal name',
    description: 'Description',
    informationalData: 'Informational data',
    additionalInformation: 'Additional information',
    location: 'Location',
    host: 'Host',
    typeLabel: 'Type',
    serviceType: 'Service type',
    subscriberVisibility: 'Subscriber visibility',
    price: 'Price',
    image: 'Image',
    chooseImage: 'Choose an image',
    uploadFailed: 'The image could not be uploaded.',
    actions: 'Actions',
    edit: 'Edit',
    create: 'Create',
    update: 'Update',
    submit: 'Submit',
    none: 'None',
    selectHost: 'Select a host',
    selectLocation: 'Select a location',
    selectServiceType: 'Select a service type',
    timeSlots: 'Time slots',
    copyLink: 'Copy link',
    linkCopied: 'Link copied.',
    copyFailed: 'The link could not be copied.',

    editEvent: 'Edit event',
    createEvent: 'Create event',
    eventSaved: 'Event saved.',
    saveFailed: 'The changes could not be saved.',
    deleteEvent: 'Delete event',
    eventDeleted: 'Event deleted.',
    deleteFailed: 'That could not be deleted.',
    deleteConfirmTitle: 'Delete this event?',
    deleteConfirmBody: 'The event and its time slots are removed. This cannot be undone.',

    timeSlotsFor: 'Time slots for',
    basic: 'Basic',
    advanced: 'Advanced',
    startTime: 'Start time',
    endTime: 'End time',
    rangeStart: 'From',
    rangeEnd: 'To',
    capacity: 'Capacity',
    shouldBeActive: 'Should the time slot be active?',
    selectedHost: 'Selected host',
    clearHost: 'Clear host',
    searchCustomer: 'Search by name, email or phone...',
    noCustomers: 'No customers matched that search.',
    slotMinutes: 'Slot length (minutes)',
    slotMinutesHint: 'How long each generated slot lasts.',
    bufferMinutes: 'Buffer (minutes)',
    bufferMinutesHint: 'Gap left between one slot ending and the next starting.',
    preview: 'Preview',
    pickStartAndEnd: 'Pick a start and an end time.',
    slotCreated: 'Time slot created.',
    slotsCreated: '{{count}} time slots created.',
    slotUpdated: 'Time slot updated.',
    slotRemoved: 'Time slot removed.',
    slotActionFailed: 'That could not be completed.',
    slotsFailed: 'The time slots could not be loaded.',
    noSlots: 'No time slots in this list.',
    until: 'until',
    min: 'min',
    overBooked: 'Over booked',
    booked: 'Booked',
    available: 'Available',
    duplicate: 'Duplicate start time',
    cloneSlot: 'Clone time slot',
    cloneSlotBody: 'A copy of this slot is created with the same time and capacity.',
    clone: 'Clone',
    removeSlot: 'Remove time slot',
    removeSlotBody: 'This cannot be undone.',
    remove: 'Remove',
    editTimeSlot: 'Edit time slot',
    conflictingEvents: 'Conflicting time slots',
    conflictsBody: 'These slots overlap the one being created.',
    createAnyway: 'Create anyway',
    slotsToCreate: '{{count}} time slots to create',
    slotsToCreateBody: 'These will be created one after another.',
    qrCode: 'Check-in QR code',
    resolution: 'Resolution',
    saveAsImage: 'Save as image',
    saveAsPdf: 'Save as PDF',

    bookings: 'Bookings',
    addBooking: 'Add booking',
    customer: 'Customer',
    shouldCreateCharge: 'Create a charge for this booking?',
    bookingCreated: 'Booking created.',
    bookingsFailed: 'The bookings could not be loaded.',
    noBookings: 'Nobody has booked this slot yet.',
    bookingActionFailed: 'That could not be completed.',
    exportList: 'Export list',
    firstName: 'First name',
    lastName: 'Last name',
    phone: 'Phone',
    email: 'Email',
    status: 'Status',
    createdOn: 'Created on',
    comments: 'Comments',
    outstanding: 'Outstanding',
    payments: 'Payments',
    paid: 'paid',
    refunded: 'refunded',
    refund: 'Refund',
    refundAmount: 'Refund amount',
    refundBody: 'Up to {{max}} can be refunded.',
    cancelPurchase: 'Cancel request',
    cancelPurchaseBody: 'A cancellation fee of {{fee}} applies.',
    purchaseCancelled: 'Payment request cancelled.',
    editBooking: 'Edit booking',
    bookingUpdated: 'Booking updated.',
    moveBooking: 'Move booking',
    moveBookingBody: 'Pick the time slot this booking should move to.',
    move: 'Move',
    bookingMoved: 'Booking moved.',
    includePast: 'Include past slots',
    noMoveTargets: 'No other time slots are available.',
    increaseCapacity: 'Add a spot if the slot is full',
    reason: 'Reason',
    willAddSpot: 'A spot will be added — capacity becomes {{capacity}}.',
    full: 'full',
    recordPayment: 'Record payment',
    recordPaymentBody: 'Record a payment taken outside the platform.',
    applyPaymentTo: 'Apply payment to',
    newPaymentOption: 'A new payment request',
    amount: 'Amount',
    method: 'Method',
    reference: 'Reference',
    adminNotes: 'Admin notes',
    paymentRecorded: 'Payment recorded.',
    createPaymentRequest: 'Create payment request',
    createPurchaseBody: 'A payment request for {{amount}} is created for this booking.',
    purchaseCreated: 'Payment request created.',
    cancelBooking: 'Cancel booking',
    cancelBookingBody: 'A cancellation fee of {{fee}} applies.',
    bookingCancelled: 'Booking cancelled.',

    past: 'past',
    paymentMethod: {
      card: 'Card',
      paypal: 'PayPal',
      placetopay: 'PlaceToPay',
      athMovil: 'ATH Movil',
      cash: 'Cash',
      athMovilManual: 'ATH Movil (manual)',
      bankTransfer: 'Bank transfer',
      check: 'Check',
      externalTerminal: 'External terminal',
      paypalInvoice: 'PayPal Invoice',
      otherOffline: 'Other (offline)',
      unknown: 'Unknown',
    },

    type: {
      rental: 'Rental',
      class: 'Class',
      other: 'Drop-in',
      yoga: 'Yoga',
      service: 'Service',
      none: 'None',
    },

    visibility: {
      free: 'Free',
      tier1: 'Tier 1',
      tier2: 'Tier 2',
      tier3: 'Tier 3',
      tier4: 'Tier 4',
      anySubscription: 'Any subscription',
    },

    bookingStatus: {
      pending: 'Pending',
      confirmed: 'Confirmed',
      cancelled: 'Cancelled',
    },

    slots: {
      activeUpcoming: 'Active upcoming',
      activePast: 'Active past',
      inactiveUpcoming: 'Inactive upcoming',
      inactivePast: 'Inactive past',
    },
    packages: 'Packages',
    addPackage: 'Add package',
    editPackage: 'Edit package',
    packageSaved: 'Package saved.',
    packageSaveFailed: 'The package could not be saved.',
    packagesFailed: 'The packages could not be loaded.',
    packageNamesRequired: 'Both the English and the Spanish name are required.',
    sortOrder: 'Order',
    retired: 'Retired',
    retire: 'Retire',
    reactivate: 'Put back on sale',
    packageReactivated: 'Package back on sale.',
    retirePackage: 'Retire this package?',
    retirePackageBody:
      'It comes off sale and stays readable, so bookings that chose it still name it. It can be put back on sale later.',
    packageRetired: 'Package retired.',
    noPackagesBody:
      'This event sells at its own price. Adding a package means customers must choose one to book.',
    firstPackageBody:
      'This is the first package on this event. Once it exists, every new booking has to choose a package.',
    packagePriceBody:
      'Changing the price changes what this costs from now on. Bookings already sold on it keep what they were sold at.',
    packagesRequiredNote:
      'Customers must choose a package to book this event.',

    changePackage: 'Change package',
    changePackageBody: 'Move this booking onto a different package of the same event.',
    noOtherPackages: 'This event has no other package on sale.',
    adjustPurchaseAmount: 'Adjust the charge',
    adjustPurchaseOn:
      'The booking is re-priced to the new package. Any difference is left as a balance to settle.',
    adjustPurchaseOff: 'Only the package changes. Nothing about the money moves.',
    packageChanged: 'Package changed.',

    package: 'Package',

    stillNeeded: 'Still needed: {{fields}}.',

  },

  gifts: {
    title: 'Membership Gifts',
    subtitle: 'Plans bought for someone else, waiting to be attached to an account.',
    searchPlaceholder: 'Search by buyer or recipient...',
    showingOf: 'Showing {{shown}} of {{total}}',
    loadFailed: 'The gifts could not be loaded.',
    noGiftsTitle: 'No gifts found',
    noGiftsBody: 'Nothing matches these filters. Try a wider date range.',

    any: 'Any',
    from: 'From',
    to: 'To',
    location: 'Location',
    status: 'Status',
    pending: 'Pending',
    assigned: 'Assigned',
    failed: 'Failed',

    purchasedOn: 'Purchased on',
    buyer: 'Buyer',
    recipient: 'Recipient',
    plan: 'Plan',
    actions: 'Actions',

    assign: 'Assign',
    assignMembership: 'Assign membership',
    assignBody: 'Choose the account this gift for {{recipient}} should be attached to.',
    user: 'User',
    searchUser: 'Search by name, email or phone...',
    noUsers: 'No users matched that search.',
    remove: 'Remove',
    assignedToast: 'Membership assigned.',
    assignFailed: 'The membership could not be assigned.',
  },

  vouchers: {
    title: 'Gift Vouchers',
    subtitle: 'The vouchers customers can buy as gifts. Each one is a pack of free bookings.',
    searchPlaceholder: 'Search by name or description...',
    showingOf: 'Showing {{shown}} of {{total}}',
    loadFailed: 'The vouchers could not be loaded.',
    noProducts: 'No vouchers yet. Create the first one to start selling gift vouchers.',
    noMatches: 'No vouchers match these filters.',

    any: 'Any',
    status: 'Status',
    onSale: 'On sale',
    offSale: 'Off sale',
    onSaleHint: 'Off sale hides it from the gift page. Vouchers already sold keep working.',
    actions: 'Actions',
    edit: 'Edit',
    issued: 'Sold',

    createProduct: 'New voucher',
    editProduct: 'Edit voucher',
    editNote: 'Changes apply to vouchers sold from now on. Vouchers already sold keep the terms they were bought with.',
    name: 'Name',
    description: 'Description',
    price: 'Price',
    priceHint: 'It covers any eligible event or package, whatever it costs.',
    uses: 'Bookings',
    usesHint: 'How many free bookings one voucher gives.',
    usesCount_one: '{{count}} booking',
    usesCount_other: '{{count}} bookings',
    validity: 'Valid for',
    validityHint: 'Counted from the day it is bought.',
    validityDays_one: '{{count}} day',
    validityDays_other: '{{count}} days',
    validityMonths_one: '{{count}} month',
    validityMonths_other: '{{count}} months',
    days: 'Days',
    months: 'Months',
    sortOrder: 'Display order',

    restrictions: 'Can be used on',
    restrictionsNone: 'Nothing selected, so it can be used on any event. Narrow it down with any combination below.',
    restrictionsHint: 'An event has to match every list that has a selection, and any one entry within each.',
    anyEventAnywhere: 'Any event',
    locations: 'Locations',
    serviceTypes: 'Service types',
    eventTypes: 'Event types',
    events: 'Specific events',
    anyLocation: 'Any location',
    anyServiceType: 'Any service type',
    anyEventType: 'Any event type',
    anyEvent: 'Any event',
    selectedCount_one: '{{count}} selected',
    selectedCount_other: '{{count}} selected',

    create: 'Create voucher',
    update: 'Save changes',
    saved: 'Voucher saved.',
    saveFailed: 'The voucher could not be saved.',
    deleteProduct: 'Remove from catalogue',
    deleteConfirmTitle: 'Remove this voucher?',
    deleteConfirmBody: 'It will no longer be sold. Vouchers already sold are not affected and can still be used. To pause sales instead, turn off On sale.',
    deleted: 'Voucher removed.',
    deleteFailed: 'The voucher could not be removed.',

    errors: {
      nameRequired: 'Enter a name in English and in Spanish.',
      usesInvalid: 'A voucher needs at least one booking.',
      validityInvalid: 'The validity must be at least 1.',
      priceInvalid: 'The price cannot be negative.',
    },

    voucher: 'Voucher',
    view: 'View',
    code: 'Code',
    holder: 'Holder',
    buyer: 'Bought by',
    from: 'From',
    sentTo: 'Sent to',
    codeOnly: 'Code handed over in person',
    inPerson: 'Sold in person',
    complimentary: 'Complimentary',
    custom: 'Custom',
    sold: 'Sold',
    kind: 'Type',
    claimedFilter: 'Claimed',
    statusFilter: {
      active: 'Active',
    },
    paid: 'Paid',
    expires: 'Expires',
    never: 'Never',
    message: 'Message',
    created: 'Created',
    adminNotes: 'Staff notes',
    usesOf: '{{used}} of {{total}} used · {{remaining}} left',
    usesOfShort: '{{used}}/{{total}} used',
    claimedOn: 'Claimed {{date}}',
    emailIn: 'Emails in {{language}}',
    issuedByStaff: 'Issued by {{name}}',
    refundedAmount: '{{amount}} refunded',
    summary: 'Summary',
    bookings: 'Bookings',
    payments: 'Payments',
    history: 'History',
    noBookings: 'No bookings have been made with this voucher yet.',
    noPayments: 'No payments recorded.',
    complimentaryNoPayment: 'Complimentary — nothing was paid for this voucher.',
    noHistory: 'Nothing has changed hands yet.',
    bookedByStaff: 'Booked by {{name}}',
    useSpent: 'Used',
    useReturned: 'Use given back',
    nobody: 'nobody',
    detailFailed: 'The voucher could not be loaded.',
    actionFailed: 'That did not go through.',
    searchUser: 'Search by name, email or phone...',
    noUsers: 'No users matched that search.',
    clearUser: 'Choose someone else',
    recipientEmail: 'Recipient email',
    recipientName: 'Recipient name',
    emailLanguage: 'Email language',
    newHolder: 'New holder',
    noExpiry: 'Never expires',
    newExpiry: 'New expiry date',
    refundAmount: 'Amount to refund',
    refundHint: 'Suggested {{suggested}} for the uses left. Up to {{refundable}} can be refunded.',
    reason: 'Reason (optional)',
    reasonRequired: 'Reason',

    state: {
      pendingPayment: 'Awaiting payment',
      paymentFailed: 'Payment failed',
      cancelled: 'Checkout abandoned',
      voided: 'Voided',
      refunded: 'Refunded',
      used: 'Used up',
      expired: 'Expired',
      claimed: 'Claimed',
      unclaimed: 'Unclaimed',
    },

    payment: {
      success: 'Received',
      pending: 'Pending',
      failed: 'Failed',
      voided: 'Voided',
      refunded: '{{amount}} refunded',
    },

    claimMethod: {
      self: 'bought for themselves',
      autoByEmail: 'matched by email',
      code: 'entered the code',
      admin: 'assigned by staff',
    },

    change: {
      claimed: 'Claimed by {{name}} ({{how}})',
      reassigned: 'Moved from {{from}} to {{to}}',
      emailChanged: 'Email changed from {{from}} to {{to}}',
    },

    actionDialog: {
      resend: {
        button: 'Resend email',
        title: 'Resend the voucher email?',
        body: 'Sends the email the recipient is waiting on again — the invitation with the code, or the "added to your account" email if it has been claimed.',
        confirm: 'Resend',
        done: 'Email sent again.',
      },
      email: {
        button: 'Change email',
        title: 'Change where it was sent',
        body: 'Nobody has claimed this voucher yet. It will be sent to the new address — straight into the account if one uses it.',
        confirm: 'Change and send',
        done: 'Voucher sent to the new address.',
      },
      reassign: {
        button: 'Reassign',
        title: 'Move to another account',
        body: 'The uses left move to the account you choose. Bookings already made stay with whoever they were for.',
        confirm: 'Move voucher',
        done: 'Voucher moved.',
      },
      extend: {
        button: 'Change expiry',
        title: 'Change the expiry date',
        body: 'Works on an expired voucher too. Reminder emails start again from the new date.',
        confirm: 'Save expiry',
        done: 'Expiry updated.',
      },
      void: {
        button: 'Void',
        title: 'Void this voucher?',
        body: 'It can no longer be used and no money is given back — for a mistake or misuse. Bookings already made with it stay. To give money back, refund instead.',
        confirm: 'Void voucher',
        done: 'Voucher voided.',
      },
      refund: {
        button: 'Refund',
        title: 'Refund this voucher',
        body: 'The money goes back through whichever way it was paid; payments taken in person are only recorded, so return those by hand. The voucher then stops working.',
        confirm: 'Refund',
        done: 'Voucher refunded.',
      },
    },

    manage: {
      title: 'Gift Vouchers',
      subtitle: 'Every voucher sold or issued — who has it, what is left, and the tools to fix one.',
      searchPlaceholder: 'Search by code, name or email...',
      noResults: 'No vouchers match these filters.',
    },

    issue: {
      title: 'Issue voucher',
      what: 'Voucher',
      fromCatalogue: 'From the catalogue',
      custom: 'Custom',
      customHint: 'A one-off voucher with its own terms. It is not added to the catalogue.',
      pickProduct: 'Choose a voucher',
      expiryPeriod: 'A period from today',
      expiryDate: 'On a date',
      who: 'Goes to',
      toAccount: 'An account',
      toEmail: 'An email',
      codeOnly: 'Code only',
      emailHint: 'If an account uses this address it goes straight in; otherwise an invitation with the code is sent.',
      codeOnlyHint: 'Nothing is sent. Give the customer the code shown on the voucher once it is created.',
      messagePlaceholder: 'Optional — shown in the email.',
      payment: 'Payment',
      complimentaryHint: 'Free — marketing, an apology, a giveaway. Reported apart from sales.',
      amount: 'Amount received',
      method: 'Paid with',
      reference: 'Reference',
      buyer: 'Customer who paid',
      buyerHint: 'Optional. They get the receipt and see it under "Vouchers I have sent".',
      submit: 'Issue voucher',
      done: 'Voucher issued.',
      failed: 'The voucher could not be issued.',
      errors: {
        product: 'Choose a voucher from the catalogue.',
        expiry: 'Choose the expiry date.',
        account: 'Choose the account it goes to.',
        email: 'Enter the recipient email.',
        amount: 'Enter the amount received, or mark it as complimentary.',
      },
    },

    report: {
      title: 'Gift Vouchers',
      subtitle: 'What was sold, what those vouchers paid for, and what is still owed as future bookings.',
      from: 'From',
      to: 'To',
      basisNotice: 'Sales are counted on the day they were paid and bookings on the day of the class. What is still owed is as of today, whatever the dates.',
      overviewGroup: 'Gift vouchers',
      salesGroup: 'Sales',
      usageGroup: 'Used',
      liabilityGroup: 'Still owed',
      salesNet: 'Voucher sales',
      salesNetTooltip: 'Money taken for vouchers in the range, less refunds. Already part of Net revenue.',
      soldSub_one: '{{count}} sold · {{refunded}} refunded',
      soldSub_other: '{{count}} sold · {{refunded}} refunded',
      collected: 'Collected',
      vouchersSold_one: '{{count}} voucher',
      vouchersSold_other: '{{count}} vouchers',
      refunded: 'Refunded',
      complimentary: 'Complimentary issued',
      complimentaryTooltip: 'Vouchers given away free in the range. Not income.',
      redeemedValue: 'Bookings paid with vouchers',
      redeemedValueTooltip: 'The price of classes and rentals in the range covered by a voucher. Not income — the money came in when the voucher was sold.',
      redemptionsSub_one: '{{count}} booking',
      redemptionsSub_other: '{{count}} bookings',
      redemptions: 'Bookings',
      outstandingValue: 'Owed as future bookings',
      outstandingValueTooltip: 'Uses still to be booked on live vouchers, valued at what was paid per use. Complimentary vouchers count as zero.',
      outstandingSub_one: '{{count}} use left',
      outstandingSub_other: '{{count}} uses left',
      outstandingUses: 'Uses left',
      expiredUnused: 'Expired unused',
      expiredUnusedTooltip: 'Uses that expired unspent on vouchers whose expiry fell in the range, valued at what was paid per use.',
    },
  },

  manageBookings: {
    title: 'Bookings',
    tabBookings: 'Bookings',
    tabPaymentReview: 'Payment review',
    subtitle: 'Every booking across events, with what was paid and what is still owed.',
    searchPlaceholder: 'Search by name, email or event...',
    showingOf: 'Showing {{shown}} of {{total}}',
    loadFailed: 'The bookings could not be loaded.',
    detailFailed: 'This booking could not be loaded.',
    noResultsTitle: 'No bookings found',
    noResultsBody: 'Nothing matches these filters. Try a wider date range.',
    export: 'Export',
    exportFailed: 'The export could not be generated.',
    nothingToExport: 'There is nothing to export for these filters.',
    actionFailed: 'That could not be completed.',

    any: 'Any',
    from: 'From',
    to: 'To',
    location: 'Location',
    eventType: 'Event type',
    serviceType: 'Service type',
    host: 'Host',
    paidHow: 'Paid how',
    status: 'Status',
    confirmed: 'Confirmed',
    pending: 'Pending',
    cancelled: 'Cancelled',
    checkedIn: 'Checked in',
    notCheckedIn: 'Not checked in',

    user: 'User',
    event: 'Event',
    when: 'When',
    amount: 'Amount',
    view: 'View',
    booking: 'Booking',
    firstTime: 'First time at this event',
    movedFrom: 'Moved from',
    money: 'Money',
    eventPrice: 'Event price',
    outstanding: 'Outstanding',
    refunded: 'Refunded',
    value: 'value',
    freeOfCharge: 'Free of charge',
    noCharge: 'No charge',
    unaccountedFor: 'Unaccounted for',
    membershipUsed: 'Membership used',
    benefitValue: 'Benefit value',
    bookedAt: 'Booked at',
    comments: 'Comments',
    additionalInformation: 'Additional information',
    moveHistory: 'Move history',
    movedBy: 'moved by',
    capacityIncreased: 'Capacity increased',
    moveBooking: 'Move booking',

    payments: 'Payments',
    noPayments: 'No payments recorded.',
    recordPayment: 'Record payment',
    recordedBy: 'recorded by',
    voidPayment: 'Void payment',
    voidPaymentBody: 'The payment is reversed and stops counting towards this booking.',
    paymentVoided: 'Payment voided.',
    voided: 'Voided',
    failed: 'Failed',
    successful: 'Successful',

    category: {
      usedBenefit: 'Used a benefit',
      paidOnline: 'Paid online',
      paidOffline: 'Paid offline',
      freeEvent: 'Free event',
      comped: 'Comped',
      awaitingPayment: 'Awaiting payment',
      refunded: 'Refunded',
      needsReview: 'Needs review',
    },
    package: 'Package',
    soldAt: 'Sold at',
    changePackage: 'Change package',
    packageHistory: 'Package history',
    noPackage: 'No package',
    changedBy: 'changed by',

    cancelBooking: 'Cancel booking',
    cancelBookingBody:
      "Cancel {{name}}'s booking? They will be told, the spot is released and online payments are refunded.",
    cancelBookingFee: 'A cancellation fee of {{fee}} is kept from the refund.',
    keepBooking: 'Keep booking',
    bookingCancelled: 'The booking was cancelled.',
  },

  manageSubscriptions: {
    title: 'Subscriptions',
    subtitle: 'Everyone currently enrolled in a plan, how they got there and what they have paid.',
    searchPlaceholder: 'Search by name, email or phone...',
    showingOf: 'Showing {{shown}} of {{total}}',
    loadFailed: 'The subscriptions could not be loaded.',
    noResults: 'No subscriptions match these filters.',
    export: 'Export',
    exportFailed: 'The export could not be generated.',
    nothingToExport: 'There is nothing to export.',

    // Filters
    location: 'Location',
    plan: 'Plan',
    status: 'Status',
    originLabel: 'Origin',
    paidHow: 'Payment',
    any: 'Any',
    all: 'All',
    active: 'Active',
    expiringSoon: 'Expiring soon',
    expired: 'Expired',
    cancelled: 'Cancelled',
    paidOnline: 'Paid online',
    paidOffline: 'Paid in person',
    belowList: 'Below list price',
    unbilled: 'Unbilled',
    sponsored: 'Sponsored',

    // Columns
    user: 'Member',
    period: 'Period',
    paid: 'Paid',
    benefits: 'Benefits',
    bookings: 'Bookings',
    actions: 'Actions',

    // Cells
    daysRemaining_one: '{{count}} day left',
    daysRemaining_other: '{{count}} days left',
    nOfMUsed: '{{used}} of {{total}} used',
    coveredByBuyer: 'Covered by buyer',
    noCharge: 'No charge',
    underListPrice: 'under list price',
    refunded: 'Refunded',
    boughtWithPromo: 'Bought on promotion',
    origin: {
      selfPurchase: 'Bought it',
      adminAssigned: 'Assigned',
      sponsored: 'Sponsored',
      gift: 'Gift',
      memberSeat: 'Family seat',
    },

    // Row actions
    edit: 'Edit',
    recordPayment: 'Record payment',
    cancelSubscription: 'Cancel subscription',
    cancelConfirmBody:
      "This ends {{name}}'s subscription now. Their remaining benefits stop being usable and nothing is refunded automatically.",
    cancelledToast: 'The subscription was cancelled.',
    actionFailed: 'That did not go through. Try again.',

    // Edit dialog
    editSubscription: 'Edit subscription',
    startDate: 'Starts',
    endDate: 'Ends',
    autoRenew: 'Renews automatically',
    countForLimit: "Counts against the plan's limit",
    comments: 'Notes',
    saved: 'The subscription was updated.',

    // Row + header actions
    view: 'View',
    newSubscription: 'New subscription',
    createSubscription: 'Create subscription',
    created: 'The subscription was created.',
    planChanged: 'The plan was changed.',

    // Sort
    sort: 'Sort by',
    sortCreatedDesc: 'Newest first',
    sortCreatedAsc: 'Oldest first',
    sortEndingSoonest: 'Ending soonest',
    sortEndingLatest: 'Ending latest',
    sortName: 'Name',
    sortPaidDesc: 'Highest paid',

    // Detail drawer
    subscription: 'Subscription',
    detailFailed: 'This subscription could not be loaded.',
    summary: 'Summary',
    payments: 'Payments',
    family: 'Family',
    dates: 'Dates',
    money: 'Money',
    listPrice: 'List price',
    createdAt: 'Created',
    coveredBy: 'Covered by',
    inactive: 'Inactive',

    // Payments tab
    noPayments: 'No payments recorded.',
    recordedBy: 'Recorded by',
    successful: 'Successful',
    failed: 'Failed',
    voided: 'Voided',
    renewal: 'Renewal',
    void: 'Void',
    refund: 'Refund',
    voidPaymentPrompt:
      'This removes a payment that was recorded by hand. The subscription goes back to owing that amount.',
    refundPaymentPrompt:
      'This sends the money back through the provider that took it. It cannot be undone here.',
    paymentVoided: 'The payment was voided.',
    paymentRefunded: 'The payment was refunded.',

    // Benefits tab
    remainingBenefits: 'Remaining benefits',
    benefitRedemptions: 'Benefits used',
    included: 'Included',
    remaining: 'Remaining',
    unlimited: 'Unlimited',
    noBenefits: 'No benefits are available to use right now.',
    noRedemptions: 'No benefits have been used yet.',

    // Bookings tab
    noBookingsWithSubscription: 'Nothing has been booked with this subscription.',
    checkedIn: 'Checked in',
    notCheckedIn: 'Not checked in',
    benefitValue: 'Benefit value',

    // Form
    who: 'Who',
    whichPlan: 'Which plan',
    when: 'When',
    options: 'Options',
    howItWasPaid: 'How it was paid',
    changePlan: 'Change plan',
    selectPlan: 'Select a plan',
    notActive: 'not active',
    noUsersFound: 'Nobody matches that search.',
    remove: 'Remove',
    overrideDates: 'Set the dates by hand',
    isActive: 'Active',
    wasSponsored: 'Was sponsored',
    allowPromo: 'Apply the current promotion',
    paidModeReceived: 'Payment received',
    paidModeSponsored: 'Sponsored — nothing to charge',
    paidModeNotYet: 'Not paid yet',
    amount: 'Amount',
    method: 'Method',
    reference: 'Reference',
    adminNotes: 'Notes',
    days: 'days',
    months: 'months',
    years: 'years',

    // The live restatement, so the whole form reads back as one sentence.
    echoGets: 'gets',
    echoFrom: 'from',
    echoTo: 'to',
    echoRecordedAs: 'Recorded as',
    echoListPriceIs: 'List price is',
    echoLoggedAsDiscount: 'so {{amount}} is logged as a discount.',
    echoSponsored: 'Sponsored, so nothing is charged.',
    echoNotPaid: 'Nothing has been collected yet.',
    echoRecordPayment: 'Record {{amount}} {{method}} for {{name}}.',
  },

  bookingPaymentHistory: {
    title: 'Payment history',
    subtitle: 'Every payment taken for a booking.',
    loadFailed: 'The payment history could not be loaded.',
    noResults: 'No payments match these filters.',
    searchPlaceholder: 'Search by name, email or event...',
    showingOf: 'Showing {{shown}} of {{total}}',

    from: 'From',
    to: 'To',
    eventType: 'Event type',
    serviceType: 'Service type',
    any: 'Any',

    name: 'Name',
    event: 'Event',
    timeSlot: 'Time slot',
    paidOn: 'Paid on',
    amount: 'Amount',
    cancelled: 'Cancelled',
  },

  itemMaster: {
    addRelation: 'Add an item',
    searchToBegin: 'Search to find an item.',
    productMetadata: 'Product details',
    whatToCopy: 'What to copy',
    pickWhatToCopy: 'Pick at least one thing to copy.',
    useThis: 'Use this one',
    metadataFailed: 'The product service could not be reached.',
    copyCatalog: 'Copy catalogue',
    copyCatalogHint:
      "Copy every item one store stocks onto other stores. Items a destination already stocks keep the destination's own settings.",
    sourceStore: 'Copy from',
    destinationStores: 'Copy to',
    copyCatalogNow: 'Copy catalogue',
    copyCatalogPrompt_one:
      "Copy {{store}}'s whole catalogue onto 1 store?",
    copyCatalogPrompt_other:
      "Copy {{store}}'s whole catalogue onto {{count}} stores?",
    catalogCopied: 'The catalogue was copied.',
    title: 'Item master',
    subtitle: 'Every product in the catalogue, above any one store.',
    loadFailed: 'The catalogue could not be searched.',
    actionFailed: 'That did not go through. Try again.',
    saved: 'Saved.',
    created: 'The item was created.',
    create: 'Create',
    createNew: 'New item',
    edit: 'Edit',
    add: 'Add',
    remove: 'Remove',
    searchPlaceholder: 'Search by name, brand or barcode...',
    showingOf: 'Showing {{shown}} of {{total}}',
    noResults: 'No items match this search.',
    clearFilters: 'Clear filters',
    tableView: 'List',
    gridView: 'Grid',
    tagged: 'Tagged',

    newItem: 'New item',
    editItem: 'Edit item',
    details: 'Details',
    archive: 'Archive',
    archived: 'Archived',
    archivePrompt:
      'Archive this item? It leaves the catalogue everywhere, and stores stocking it stop showing it.',
    restore: 'Restore',
    restored: 'The item was restored.',
    primaryGtin: 'Primary barcode',
    createdOn: 'Created',
    gtinHint: 'The primary barcode. Manage the rest under Barcodes.',
    internalId: 'Internal id',
    brand: 'Brand',
    name: 'Name',
    description: 'Description',
    division: 'Division',
    rank: 'Rank',
    rankHint: 'Higher ranks surface earlier in search.',
    searchTags: 'Search terms',
    searchTagsHint: 'Extra words this item should be found by.',
    packagingSensitive: 'Packaging sensitive',
    temperatureSensitive: 'Temperature sensitive',
    alcoholicBeverage: 'Alcohol',
    perishable: 'Perishable',
    isEbt: 'EBT',
    idRequired: 'ID required',
    hasPluCode: 'Has a PLU code',
    affectsInventory: 'Affects inventory',

    stores: 'Stores',
    noStores: 'No store stocks this item yet.',
    pickAStore: 'Pick a store',
    assignToStore: 'Assign',
    assignToAll: 'Assign to all',
    assigned: 'The item was assigned.',
    copyConfig: "Copy an item's configuration to the other stores",
    copyConfigHint:
      'Take one store as the model and set the others to match it: price, barcode, section, aisle, visibility, availability, taxes, dietary facts and selling hours.',
    copyFrom: 'Take the configuration from',
    pickSourceStore: 'Pick the store to copy from...',
    copyTo: 'Apply it to',
    selectAll: 'Select all',
    clearAll: 'Clear',
    copyNow: 'Copy configuration',
    copyConfigPrompt_one:
      "Set the other store to match {{store}}? Its own configuration for this item is overwritten.",
    copyConfigPrompt_other:
      "Set {{count}} stores to match {{store}}? Their own configuration for this item is overwritten.",
    configCopied: 'The configuration was copied.',
    forcePriceUpdate: 'Re-pull prices from the price integration',
    pricesRefreshed: 'The prices were re-pulled.',
    visible: 'Visible',
    hidden: 'Hidden',
    available: 'Available',
    unavailable: 'Unavailable',
    aisle: 'Aisle',
    onHand: 'on hand',

    gtins: 'Barcodes',
    newGtin: 'Add a barcode',
    noGtins: 'No barcodes yet.',
    gtinAlreadyListed: 'That barcode is already listed.',
    primary: 'Primary',
    makePrimary: 'Make primary',

    media: 'Pictures',
    mediaFailed: 'The pictures could not be loaded.',
    pictures: 'Pictures',
    videos: 'Videos',
    noPictures: 'No pictures yet.',
    noVideos: 'No videos yet.',
    imageUrl: 'Paste an image URL',
    videoUrl: 'Paste a video URL',
    thumbnailUrl: 'Paste a thumbnail image URL',
    addByUrl: 'Add by URL',
    uploadImage: 'Upload an image',
    uploadFailed: 'The image could not be uploaded.',
    mainImage: 'Shown first',
    moveUp: 'Move up',
    moveDown: 'Move down',
    saveOrder: 'Save order',
    orderSaved: 'The order was saved.',

    filters: 'Filters',
    manageFilters: 'Filters',
    noFilters: 'No filters have been set up.',
    applied: 'On',
    notApplied: 'Off',
    nItems_one: '{{count}} item',
    nItems_other: '{{count}} items',

    recommendations: 'Recommended with',
    substitutes: 'Substitutes',
    noRecommendations: 'Nothing is recommended with this item.',
    noSubstitutes: 'Nothing substitutes for this item.',

    logs: 'History',
    logsFailed: 'The history could not be loaded.',
    noLogs: 'Nothing has been changed yet.',
    logDetails: 'Details',
    logChanges: 'What changed',
    logAllFields: 'All fields',
    logRaw: 'Raw data',
    logNoChanges: 'This update changed none of the recorded values.',
    logNoPrevious:
      'No earlier entry is loaded to compare with. Open the previous page to see what this changed.',
    logShowEmpty_one: 'Show {{count}} empty field',
    logShowEmpty_other: 'Show {{count}} empty fields',
    logHideEmpty: 'Hide empty fields',
    logCopyData: 'Copy raw data',
  },

  businessItem: {
    general: 'General',
    pricing: 'Pricing',
    hours: 'Hours',
    flags: 'Flags',

    primaryUpc: 'Primary barcode',
    upcs: 'Barcodes',
    newUpc: 'Add a barcode',
    noUpcs: 'This store has no barcodes for the item.',
    resolvePrimary: 'Work out the primary',
    group: 'Group',
    groupHint: 'The department this store files the item under.',
    aisle: 'Aisle',
    section: 'Section',
    pickSection: 'Pick a section',
    pickFromList: 'Pick from the list',
    typeNew: 'Type a new one',
    minimum: 'Minimum',
    maximum: 'Maximum',
    stepAmount: 'Step',
    pack: 'Pack',
    randomWeight: 'Sold by weight',
    weightType: 'Weight unit',
    unitPerPackage: 'Units per package',
    customSearchTags: 'Store search terms',
    customSearchTagsHint: 'Extra words this store should find the item by.',
    itemType: 'Item type',
    inStore: 'In store',
    externalSupplier: 'External supplier',

    munTax: 'Municipal tax %',
    stateTax: 'State tax %',
    basePrice: 'Base price',
    promoPrice: 'Promotional price',
    price: 'Price',
    multiple: 'Multiple',
    cost: 'Cost',
    costOfBox: 'Cost of a box',
    priceMethod: 'Price method',
    unit: 'Unit',
    split: 'Split',
    taxRule: 'Tax rule',
    taxNone: 'None',
    taxMunicipal: 'Municipal',
    taxState: 'State',
    taxBoth: 'Both',
    startDate: 'Starts',
    endDate: 'Ends',
    addPrice: 'Add a price',
    removePrice: 'Remove price',
    priceNameTaken: 'Another price already goes by that name.',
    priceOrderTaken: 'Another price already sits in that order.',
    editPrice: 'Edit price',
    noPrices: 'This store has no prices for the item.',
    unnamedPrice: 'Unnamed price',
    inactive: 'Inactive',
    active: 'Active',
    ordering: 'Order',

    limitDate: 'Available until',
    hourDelay: 'Hours of notice',
    minuteDelay: 'Minutes of notice',
    availableOnMondays: 'Mondays',
    availableOnTuesdays: 'Tuesdays',
    availableOnWednesdays: 'Wednesdays',
    availableOnThursdays: 'Thursdays',
    availableOnFridays: 'Fridays',
    availableOnSaturdays: 'Saturdays',
    availableOnSundays: 'Sundays',

    allowedInLayaway: 'Allowed in layaway',
    hideInKiosk: 'Hide in the kiosk',
    hideInSelfCheckout: 'Hide in self-checkout',
    promoted: 'Promoted',
    madeLocally: 'Made locally',
    customDepartment: 'Custom department',
    dietary: 'Dietary',
    natural: 'All natural',
    antioxidant: 'Antioxidant',
    glutenFree: 'Gluten free',
    grassFed: 'Grass fed',
    keto: 'Keto',
    lactoseFree: 'Lactose free',
    nonGmo: 'Non-GMO',
    nutFree: 'Nut free',
    organic: 'Organic',
    sugarFree: 'Sugar free',
    vegan: 'Vegan',
    vegetarian: 'Vegetarian',

    modifiers: 'Requirements, sides and add-ons',
    modifiersGlobal:
      'This client keeps one set for every store, so a change here reaches them all.',
    modifiersPerStore: 'These belong to this store alone.',
    requirements: 'Requirements',
    sides: 'Sides',
    addons: 'Add-ons',
    noneAssigned: 'Nothing is assigned yet.',
    nothingToAssign: 'There is nothing left to assign.',
    searchModifiers: 'Search...',

    archivePrompt:
      'Stop {{store}} stocking this item? It comes off that store only — every other store goes on selling it.',
    restorePrompt: 'Have {{store}} stock this item again?',
    archived: 'The item was taken off that store.',
    applyToMultiple: 'Apply the same changes to',
    link: 'Link',
    linkCopied: 'The link was copied.',
  },

  itemRegistration: {
    title: 'Item registration',
    subtitle:
      'The shared library a store\u2019s catalogue is assigned from. Describe an item once, stock it anywhere.',
    loadFailed: 'The library could not be searched.',
    actionFailed: 'That did not go through. Try again.',
    saved: 'Saved.',
    createNew: 'New item',
    create: 'Create',
    edit: 'Edit',
    add: 'Add',
    remove: 'Remove',
    submit: 'Assign',
    searchPlaceholder: 'Search by name, brand or barcode...',
    showingOf: 'Showing {{shown}} of {{total}}',
    itemNotFound: 'Nothing in the library matches that',
    itemNotFoundBody:
      'Try a different barcode or name. If the item genuinely is not here yet, it has to be registered before a store can stock it.',

    assigned: 'In the catalogue',
    notAssigned: 'Not assigned',
    assign: 'Assign',
    assignedToast: 'The item was assigned.',
    assignToCatalog: 'Assign to a catalogue',
    cost: 'Cost',
    price: 'Price',
    assignToMultipleStores: 'Assign to several stores',
    stores: 'Stores',
    selectAll: 'Select all',
    clearAll: 'Clear all',

    newItem: 'New item',
    editItem: 'Edit item',
    archive: 'Archive',
    archivePrompt:
      'Archive this item? It leaves the library, but catalogues that already took it keep what they have.',
    archived: 'The item was archived.',
    primaryGtinHint: 'The primary barcode. Manage the rest under Barcodes.',

    brand: 'Brand',
    name: 'Name',
    description: 'Description',
    searchTags: 'Search terms',
    searchTagsHint: 'Extra words this item should be found by.',
    division: 'Division',
    group: 'Group',
    suggestedCategory: 'Suggested category',
    pack: 'Pack',
    taxRule: 'Tax rule',
    stateTax: 'State tax',
    municipalTax: 'Municipal tax',
    packagingSensitive: 'Packaging sensitive',
    temperatureSensitive: 'Temperature sensitive',
    alcoholicBeverage: 'Alcohol',
    perishable: 'Perishable',
    isEbt: 'EBT',
    idRequired: 'ID required',
    hasPluCode: 'Has a PLU code',
    hasRandomWeight: 'Sold by weight',

    gtins: 'Barcodes',
    newGtin: 'Add a barcode',
    noGtins: 'No barcodes yet.',
    gtinAlreadyListed: 'That barcode is already listed.',
    primary: 'Primary',
    makePrimary: 'Make primary',

    media: 'Media',
    mediaFailed: 'The pictures could not be loaded.',
    pictures: 'Pictures',
    videos: 'Videos',
    noPictures: 'No pictures yet.',
    noVideos: 'No videos yet.',
    addPicture: 'Add a picture',
    addVideo: 'Add a video',
    addByUrl: 'Add by URL',
    imageUrl: 'Paste an image URL',
    videoUrl: 'Paste a video URL',
    thumbnailUrl: 'Paste a thumbnail image URL',
    uploadImage: 'Upload an image',
    uploadFailed: 'The image could not be uploaded.',
    mainImage: 'Shown first',
    moveUp: 'Move up',
    moveDown: 'Move down',
    saveOrder: 'Save order',
    orderSaved: 'The order was saved.',

    applyImages: 'Push pictures',
    applyImagesBody:
      'Send this item\u2019s pictures to every catalogue that took it. Adding keeps what each store already has; replacing throws it away first.',
    addImages: 'Add to existing',
    replaceImages: 'Replace',
    imagesApplied: 'The pictures were sent.',
  },

  itemPrices: {
    title: 'Item prices',
    subtitle: 'What the POS says an item costs, and how it reaches the catalogue.',
    loadFailed: 'The prices could not be searched.',
    actionFailed: 'That did not go through. Try again.',
    reload: 'Reload',
    close: 'Close',
    createNew: 'Request a new item',
    create: 'Create request',
    showingOf: 'Showing {{shown}} of {{total}}',
    noResults: 'No prices match this search.',

    anyStore: 'Any store',
    byGeneral: 'General',
    byItemNumber: 'Item number',
    searchGeneral: 'Search by name, brand or description...',
    searchGtin: 'Search by GTIN...',
    searchItemNumber: 'Search by item number...',
    pickStoreToConnect: 'Pick a store to connect prices to its catalogue.',

    updatePrice: 'Apply price',
    priceApplied: 'The price was applied.',
    connect: 'Connect',
    connected: 'Connected',
    promo: 'Promo',

    details: 'Details',
    pricing: 'Pricing',
    flags: 'Flags',
    itemNumber: 'Item number',
    internalName: 'Internal name',
    department: 'Department',
    aisle: 'Aisle',
    quantityOnHand: 'On hand',
    taxRuleLabel: 'Tax rule',
    basePrice: 'Base price',
    baseMultiple: 'Base multiple',
    basePriceMethod: 'Price method',
    cost: 'Cost',
    pack: 'Pack',
    stateTax: 'State tax',
    municipalTax: 'Municipal tax',
    promoPrice: 'Promo price',
    promoMultiple: 'Promo multiple',
    promoPriceMethod: 'Promo price method',
    promoStartDate: 'Promo starts',
    promoEndDate: 'Promo ends',
    idRequired: 'ID required',
    alcoholicBeverage: 'Alcohol',
    isVisible: 'Visible',
    isAvailable: 'Available',
    isDeleted: 'Deleted',
    isEbt: 'EBT',
    editedOn: 'Edited',
    createdOn: 'Created',
    unknown: 'Unknown',

    taxRule: {
      none: 'No tax',
      municipal: 'Municipal only',
      state: 'State only',
      both: 'Municipal and state',
    },
    priceMethod: {
      unit: 'Unit pricing',
      split: 'Split package',
    },
    weightType: {
      lb: 'LB',
      unit: 'Unit',
    },

    foundItems: 'Possible matches',
    foundPotentialMatches:
      'These catalogue items look like this price. Connect it to one, or request a new item.',
    noPotentialMatch:
      'Nothing in the catalogue looks like this price. Request a new item for it.',
    associate: 'Connect to this',
    associateEcho: 'Connecting the price for {{price}} to {{item}}.',
    connectedToast: 'The price was connected.',
    newItemRequest: 'Request a new item',
    created: 'The request was created.',

    theItem: 'The item',
    internalId: 'Internal id',
    brand: 'Brand',
    name: 'Name',
    description: 'Description',
    searchTags: 'Search terms',
    searchTagsHint: 'Extra words this item should be found by.',
    itemImage: 'Image',
    thumbnail: 'Thumbnail',
    uploadImage: 'Upload an image',
    uploadFailed: 'The image could not be uploaded.',
    packagingSensitive: 'Packaging sensitive',
    temperatureSensitive: 'Temperature sensitive',
    perishable: 'Perishable',

    whereItSits: 'Where it sits',
    inStoreId: 'In-store id',
    category: 'Category',
    selectCategory: 'Pick a category',
    howItIsSold: 'How it is sold',
    randomWeight: 'Sold by weight',
    weightTypeLabel: 'Weight unit',
    minimum: 'Minimum',
    maximum: 'Maximum',
    stepAmount: 'Step',
    unitPerPackage: 'Units per package',
    madeLocally: 'Made locally',

    dietary: 'Dietary claims',
    natural: 'All natural',
    lactoseFree: 'Lactose free',
    sugarFree: 'Sugar free',
    keto: 'Keto',
    organic: 'Organic',
    vegan: 'Vegan',
    vegetarian: 'Vegetarian',
    glutenFree: 'Gluten free',
    grassFed: 'Grass fed',
    antioxidant: 'Antioxidant',
    nutFree: 'Nut free',
    nonGmo: 'Non-GMO',
  },

  staff: {
    title: 'Staff',
    subtitle: 'Who can work in this store, and what they are allowed to do.',
    loadFailed: 'The staff list could not be loaded.',
    actionFailed: 'That did not go through. Try again.',
    saved: 'Saved.',
    refresh: 'Refresh',
    noStaff: 'Nobody works in this store yet.',
    filterPlaceholder: 'Filter by name, email or phone...',
    hideInactive: 'Hide inactive',
    active: 'Active',
    inactive: 'Inactive',
    admin: 'Admin',
    regular: 'Regular',
    since: 'Since',
    yes: 'Yes',
    no: 'No',

    addStaff: 'Add somebody',
    addStaffHint:
      'Find an account that already exists on the platform and give it access to this store.',
    searchPlaceholder: 'Search by name or email...',
    searchToBegin: 'Search to find an account.',
    noUsers: 'No account matches that search.',
    add: 'Add',
    userAdded: 'They now have access to this store.',

    provisionalCode: 'Provisional manager code',
    provisionalCodePrompt:
      'Create a one-off manager code for this store? It is copied to the clipboard.',
    codeCopied: 'Manager code {{code}} — copied.',
    managerCode: 'Manager code',
    barcodeUnavailable: 'This code cannot be drawn as a barcode.',
    generateNewCode: 'Generate a new code',
    generateNewCodePrompt:
      "Generate a new manager code for {{name}}? Their current code stops working.",
    newCodeCreated: 'A new code was created.',
    print: 'Print',
    printBlocked: 'The print window was blocked. Allow pop-ups and try again.',
    sendPin: 'Send login PIN',
    pinSent: 'The PIN was sent.',
    pinCopied: 'PIN {{pin}} — copied.',

    privileges: 'Privileges',
    notifications: 'Notifications',
    roles: 'Roles',
    noRoles: 'They hold no platform roles.',
    rolesHint: 'Roles come from the platform and are not set per store.',
    isActive: 'Active',
    isActiveHint: 'Turning this off stops them signing in to this store.',
    hiddenFromStaff: 'Hidden from the staff list',
    notEditableHere: 'Reported, but set elsewhere',
    notEditableHint:
      'The update this page uses has no field for these, so they are shown as they stand.',
    canEditOrders: 'Edit orders',
    canCancelOrders: 'Cancel orders',
    canProvideCredit: 'Give credit',
    canCreateMenuItems: 'Create products',
    canEditMenuItems: 'Edit products',
    canEditPrice: 'Edit prices',
    canEditStore: 'Edit the store',
    canEditLocation: 'Edit locations',
    canEditUsers: 'Edit staff',
    canEditPayments: 'Edit payments',
    canViewAnalytics: 'View analytics',
    canActivateUsers: 'Activate accounts',
    forceSms: 'Force SMS',
    getNotifications: 'All notifications',
    getOrderNotifications: 'New orders',
    getPickUpNotifications: 'Pick-ups',
    getDeliveryNotifications: 'Deliveries',
    getSensitiveNotifications: 'Sensitive',
    getSensitiveFinanceNotifications: 'Sensitive finance',
    getLaneNotifications: 'POS lanes',
    getLaneNotificationsHint:
      'The staff list does not report this one, so it starts off and has to be set each time.',

    performance: 'Performance',
    performanceFailed: 'The performance figures could not be loaded.',
    picking: 'Picking',
    packing: 'Packing',
    from: 'From',
    to: 'To',
    orderCount: 'Orders',
    itemsPerOrder: 'Items per order',
    itemsPerMinute: 'Items per minute',
    allItems: 'All items',
    averageTime: 'Average time',
    minutes: 'min',
  },

  timesheetTypes: {
    title: 'Timesheet types',
    subtitle: 'The kinds of shift a timesheet entry can be.',
    loadFailed: 'The types could not be loaded.',
    actionFailed: 'That did not go through. Try again.',
    saved: 'Saved.',
    refresh: 'Refresh',
    noTypes: 'No types have been set up yet.',
    newType: 'New type',
    editType: 'Edit type',
    create: 'Create',
    nameEn: 'Name (English)',
    nameEs: 'Name (Spanish)',
    default: 'Default',
    defaultHint: 'The type a shift starts as when nobody picks one.',
    delete: 'Delete',
    deletePrompt: 'Delete {{name}}? Shifts already recorded under it keep it.',
    deleted: 'The type was deleted.',
  },

  timesheets: {
    title: 'Timesheets',
    subtitle: 'Who worked when.',
    loadFailed: 'The timesheets could not be loaded.',
    actionFailed: 'That did not go through. Try again.',
    saved: 'Saved.',
    created: 'The entry was created.',
    create: 'Create',
    refresh: 'Refresh',
    print: 'Print',
    printBlocked: 'The print window was blocked. Allow pop-ups and try again.',
    noEntries: 'Nobody worked in this range.',

    from: 'From',
    to: 'To',
    employee: 'Employee',
    anyEmployee: 'Anyone',
    pickEmployee: 'Pick somebody',
    type: 'Type',
    anyType: 'Any type',
    pickType: 'Pick a type',
    inactive: 'inactive',

    view_daily: 'By day',
    view_person: 'By person',
    view_entry: 'By shift',
    who: 'Who',
    day: 'Day',
    startTime: 'Start',
    endTime: 'End',
    endTimeHint: 'Leave blank for a shift that is still open.',
    stillOpen: 'still open',
    hours: 'Hours',
    minutes: 'min',
    totalHours: 'Total hours',
    totals_one: 'Totals — {{count}} row',
    totals_other: 'Totals — {{count}} rows',
    comment: 'Comment',
    toggleEntries: 'Show the shifts',
    nShifts_one: '{{count}} shift',
    nShifts_other: '{{count}} shifts',

    layout_list: 'List',
    layout_calendar: 'Calendar',
    previousMonth: 'Previous month',
    nextMonth: 'Next month',
    thisMonth: 'This month',
    moreShifts: '+{{count}} more',
    pickDay: 'Pick a day to see its shifts.',
    noShiftsThatDay: 'Nobody worked this day.',

    forPayroll: 'For payroll',
    hourlyRate: 'Hourly rate',
    deductionPercent: 'Deduction %',
    payrollHint:
      'Unpaid lunch comes off the hours, and the deduction is a percentage of what is left. Lunch is entered here and is not saved.',
    lunch: 'Lunch (hours)',
    gross: 'Gross',
    deduction: 'Deduction',
    net: 'Net',

    newEntry: 'New entry',
    editEntry: 'Edit entry',
    delete: 'Delete',
    deletePrompt: 'Delete this entry? The hours come off the total.',
    deleted: 'The entry was deleted.',

    clockedIn: 'On the clock',
    nobodyClockedIn: 'Nobody is clocked in right now.',
    onTheClock: 'Working',
    since: 'Since',
  },

  posLanes: {
    title: 'POS lanes',
    subtitle: 'The tills in this store.',
    loadFailed: 'The lanes could not be loaded.',
    actionFailed: 'That did not go through. Try again.',
    saved: 'Saved.',
    created: 'The lane was created.',
    create: 'Create',
    refresh: 'Refresh',
    noLanes: 'This store has no lanes yet.',
    noneMatch: 'No lane matches this search.',
    searchPlaceholder: 'Search by tag, device or terminal...',

    allLanes: 'Lanes',
    blocked: 'Blocked',
    blockedHint: 'A blocked lane stays set up but cannot trade.',
    outdated: 'Behind on version',
    latestVersion: 'Latest is {{version}}',
    untagged: 'Untagged lane',
    neverReported: 'Never reported',
    noDevice: 'No device reported yet',
    lastTransaction: 'Last sale',
    version: 'Version',
    device: 'Device',
    deviceName: 'Device name',
    terminalId: 'Terminal id',
    createdOn: 'Created',

    sortTag: 'Sort by tag',
    sortVersion: 'Sort by version',
    sortLastTransaction: 'Sort by last sale',

    newLane: 'New lane',
    editLane: 'Edit lane',
    tag: 'Tag',
    tagPlaceholder: 'Lane 1',
    tagHint: 'What the staff call this till.',
    remove: 'Remove',
    removePrompt:
      'Remove {{tag}}? Its past sales stay on record, but the till stops working.',
    removed: 'The lane was removed.',
  },

  /** Only shown for the client that has it (`VITE_HAS_3PL_DELIVERIES`). */
  deliveries: {
    title: 'Deliveries',
    searchTab: 'Search',
    sortingTab: 'Route order',

    from: 'From',
    to: 'To',
    provider: 'Provider',
    anyProvider: 'Any provider',
    zone: 'Zone',
    anyZone: 'Any zone',
    statusFilter: 'Status',
    status: {
      all: 'All',
      // Everything not yet delivered or cancelled.
      pending: 'Open',
      assigned: 'Driver assigned',
      inTransit: 'In transit',
      delivered: 'Delivered',
      cancelled: 'Cancelled',
    },

    resultCount_one: '{{count}} delivery',
    resultCount_other: '{{count}} deliveries',
    delivered: 'Delivered',
    unassigned: 'No driver',
    bagCount_one: '{{count}} bag',
    bagCount_other: '{{count}} bags',

    export: 'Export',
    exporting: 'Exporting…',
    exportFailed: 'The export could not be created.',
    loadFailed: 'The deliveries could not be loaded.',
    noResultsTitle: 'No deliveries match',
    noResultsBody: 'Try a different status, a wider date range, or another zone.',

    noZones: 'This store has no active delivery zones.',
    noStops: 'No open deliveries in this zone.',
    moveStop: 'Move {{name}}',
    unsavedRoute: 'Unsaved changes',
    saveRoute: 'Save order',
    routeSaved: 'Route order saved.',
    routeFailed: 'The route order could not be saved.',
  },

  cashDrawer: {
    title: 'Cash drawer report',
    subtitle: 'What each till took, and whether it balanced.',
    loadFailed: 'The report could not be loaded.',
    actionFailed: 'That did not go through. Try again.',
    saved: 'Saved.',
    refresh: 'Refresh',
    noCheckIns: 'No drawer was opened in this range.',

    from: 'From',
    to: 'To',
    state: 'State',
    anyState: 'Any state',
    openOnly: 'Still open',
    closedOnly: 'Closed',
    underOnly: 'Short',
    overOnly: 'Over',
    lane: 'Lane',
    anyLane: 'Any lane',
    employee: 'Employee',
    anyEmployee: 'Anyone',

    open: 'Open',
    closed: 'Closed',
    balanced: 'Balanced',
    over: 'Over by',
    under: 'Short by',
    unknownUser: 'Unknown employee',
    unknownLane: 'Unknown lane',

    sales: 'Sales',
    net: 'Net',
    drops: 'Drops',
    balance: 'Balance',
    cashIn: 'Cash in',
    cashOut: 'Cash out',
    openingAmount: 'Counted at opening',
    closingAmount: 'Counted at close',
    opening: 'Opening',
    counted: 'Counted',
    expected: 'Should hold',
    notCounted: 'Not counted yet',
    startTime: 'Opened',
    endTime: 'Closed',

    theCount: 'The count',
    movements: 'Movements',
    comments: 'Comments',
    noComments: 'Nobody has left a note on this drawer.',
    newComment: 'Leave a note...',
    noMovements: 'No cash moved through this drawer.',
    addMovement: 'Add a movement',
    editMovement: 'Edit movement',
    isDropIn: 'Drop to the safe',
    isDropInHint: 'A drop takes cash out of the till without a sale.',
    drop: 'Drop',
    delete: 'Delete',
    deletePrompt: 'Delete this movement? The balance is recalculated without it.',
    deleted: 'The movement was deleted.',

    recalculate: 'Work the closing figure out again',
    recalculateHint:
      'Rebuilds what the till should hold from its movements. Preview first — it reports what would change without changing it.',
    preview: 'Preview',
    previewResult: '{{changed}} of {{evaluated}} would change.',
    applyRecalculation: 'Apply',
    recalculated: 'The figures were worked out again.',
  },

  businessInfo: {
    title: 'Business info',
    subtitle: "The store's own details.",
    loadFailed: 'The store details could not be loaded.',
    actionFailed: 'That did not go through. Try again.',
    saved: 'Saved.',

    general: 'General',
    pickup: 'Pickup',
    place: 'Location',

    theBusiness: 'The business',
    theLocation: 'Address and contact',
    theWindow: 'The pickup window',
    theFee: 'The pickup fee',
    theMap: 'Where it is',

    businessName: 'Business name',
    backgroundImage: 'Background image',
    imageUrl: 'Paste an image URL',
    upload: 'Upload',
    uploadFailed: 'The image could not be uploaded.',
    isAlpha: 'Alpha store',
    isBeta: 'Beta store',
    visibleInBeta: 'Visible to beta users',

    locationName: 'Location name',
    storePhone: 'Store phone',
    address1: 'Address',
    address2: 'Address line 2',
    email: 'Order email',
    smsAudienceId: 'SMS audience id',
    smsAudienceIdHint: 'The list this store texts from.',
    allowDelivery: 'Delivery',
    allowPickup: 'Pickup',
    posIntegration: 'External POS integration',

    pickupStartHour: 'First pickup hour',
    pickupEndHour: 'Last pickup hour',
    pickupBuffer: 'Buffer',
    pickupBufferHint: 'Minutes between an order and the earliest slot it can take.',
    slotRange: 'Slot length',
    slotRangeHint: 'How many minutes one pickup slot covers.',
    pickupLimit: 'Orders per hour',
    pickupLimitHint: 'How many pickups the store will accept in an hour.',
    futureDays: 'Days ahead',
    futureDaysHint: 'How far ahead a customer may book a pickup.',
    preventSameDay: 'No same-day slots',
    preventSameDayHint: 'Customers cannot pick a slot for today.',

    storeFee: 'Store pickup fee',
    storeFeeType: 'Fee type',
    feeNone: 'None',
    feeFixed: 'Fixed',
    feePercentage: 'Percentage',
    globalFee: 'Platform order fee',
    globalFeeHint: 'Set above this store and not changed here.',

    city: 'City',
    cityHint: 'Reported by the store, but the update has no field for it.',
    directions: 'Directions',
    lat: 'Latitude',
    lon: 'Longitude',
    noPin: 'This location has no coordinates yet.',
    noMapKey: 'No Google Maps key is configured, so the map is unavailable.',
    mapFailed:
      'The map could not be loaded. Check that the key allows the Maps JavaScript API and this site.',
    mapHint: 'Drag the pin, or click the map, to move it.',
    mapNoPinHint: 'Click the map to place this store.',
    openInMaps: 'Open in Google Maps',
  },

  storeOptions: {
    title: 'Store options',
    subtitle: 'How this store behaves.',
    loadFailed: 'The store options could not be loaded.',
    noLocation: 'This store has no location yet. Add its address under General first.',
    actionFailed: 'Some of that did not go through. Check and try again.',
    saved: 'Saved.',

    storefront: 'Storefront',
    notifications: 'Notifications',
    orders: 'Orders',
    products: 'Products',
    layaway: 'Layaway',
    giftCards: 'Gift cards',
    tax: 'Tax credentials',

    departments: 'Departments shown',
    sponsoredDepartment: 'Sponsored',
    offersDepartment: 'Offers',
    recentlyAddedDepartment: 'Recently added',
    healthyDepartment: 'Healthy',
    madeLocallyDepartment: 'Made locally',
    searchFilters: 'Search filters shown',
    defaultFilters: 'Default filters',
    healthyFilters: 'Healthy filters',
    brandFilters: 'Brand filters',
    customFilters: 'Custom filters',

    paypal: 'PayPal',
    paypalHint: "This store's own PayPal application, used for its payments.",
    paypalClientId: 'PayPal client id',
    paypalClientSecret: 'PayPal client secret',
    reveal: 'Show',
    hide: 'Hide',
    whoIsTold: 'What the store is told about',
    notifyStore: 'Email the store',
    notifyStoreHint: 'Order emails go to the address on Business info.',
    delayInOrders: 'Orders running late',
    pickingFinished: 'Picking finished',
    packingFinished: 'Packing finished',

    takingOrders: 'Taking orders',
    codePrefix: 'Confirmation code prefix',
    codePrefixHint: 'Sits in front of every order number from this store.',
    minimumPickup: 'Minimum pickup order',
    minimumDelivery: 'Minimum delivery order',
    additionalItemLimit: 'Additional item limit',
    additionalItemLimitHint: 'How much a customer may add after ordering.',
    allowAdditionalOrders: 'Allow items to be added after ordering',
    allowOrderModification: 'Allow orders to be changed',
    authAtOrderEntry: 'Authorise the card when the order is placed',
    authAtOrderEntryHint: 'Otherwise the card is charged when the order is packed.',
    managedBins: 'Bins managed by the parent store',
    cancellation: 'Cancellation',
    cancellationFee: 'Cancellation fee',
    feeType: 'Fee type',
    feeNone: 'None',
    feeFixed: 'Fixed',
    feePercentage: 'Percentage',
    pickingAndPacking: 'Picking and packing',
    markPickedAsPacked: 'Mark picked items as packed',
    markNotPackedAsOut: 'Mark unpacked items as out of stock',
    payouts: 'Payouts',
    stripeDelay: 'Stripe transfer delay',
    paypalDelay: 'PayPal transfer delay',
    transferDelayHint: 'Days held before the money is transferred.',

    outOfStock: 'Out of stock',
    oosTrigger: 'Take items out of stock automatically',
    oosTriggerHint: 'After enough shoppers report one missing.',
    oosLimit: 'Reports needed',
    oosLimitHint: 'How many reports it takes before an item is pulled.',
    whatShoppersSee: 'What shoppers see',
    subsByDefault: 'Substitutions allowed by default',
    subsByDefaultHint: 'A shopper can still turn them off per item.',
    hideNoPictures: 'Hide products with no picture',

    hasLayaway: 'Layaway available',
    hasLayawayHint: 'Customers can put items aside and pay over time.',
    layawayExpirationDays: 'Days before it expires',
    layawayExpirationDaysHint: 'How long a layaway may stand unpaid.',
    maxLayaways: 'Layaways per customer',
    maxLayawaysHint: 'How many one customer may have open at once.',
    layawayAmounts: 'What it costs',
    layawayServiceFee: 'Service fee',
    layawayInitialPayment: 'Payment to open one',
    layawayPaymentMinimum: 'Minimum payment',
    layawayPenalty: 'Cancellation penalty',

    activationFee: 'Gift card activation fee',
    activationFeeHint: 'Charged once when a gift card is activated.',

    taxHint:
      'Who issues this store\u2019s IVU control numbers, and the credentials it signs with.',
    taxProvider: 'Tax provider',
    taxProvider_0: 'None',
    taxProvider_1: 'Provided by the POS',
    taxProvider_3: 'Softek',
    merchantId: 'Merchant id',
    terminalId: 'Terminal id',
    terminalPassword: 'Terminal password',
  },

  paymentConfig: {
    title: 'Payment options',
    subtitle: 'The ways this store can be paid.',
    loadFailed: 'The payment options could not be loaded.',
    actionFailed: 'That did not go through. Try again.',
    saved: 'Saved.',
    create: 'Create',
    noOptions: 'This store cannot take any payment yet.',
    unnamed: 'Unnamed option',
    active: 'Active',
    inactive: 'Inactive',
    from: 'from',
    nowhere: 'Accepted nowhere',

    newOption: 'New option',
    createFrom: 'Create from an existing option',
    createFromPlaceholder: 'Start from...',
    createFromHint:
      'Copies everything except the name, which gets a suffix, and Active, which starts off so the copy cannot go live unreviewed.',
    copyName: '{{name}} (copy)',
    editOption: 'Edit option',
    createDefaults: 'Create defaults',
    createDefaultsHint:
      'Lay down a starting set of payment options. It adds what you tick and leaves anything already set up alone.',
    defaultsCreated: 'The payment options were created.',
    delete: 'Delete',
    deletePrompt: 'Delete {{name}}? Customers can no longer pay this way.',
    deleted: 'The option was deleted.',

    name: 'Name',
    image: 'Image',
    imageUrl: 'Paste an image URL',
    uploadFailed: 'The image could not be uploaded.',
    paymentType: 'Payment type',
    provider: 'Processed by',
    selector: 'Card type',
    chargeType: 'Charged',
    minCharge: 'Minimum charge',
    minChargeHint: 'Below this, the option is not offered.',

    whereAccepted: 'Where it is accepted',
    allowedInRegularService: 'Pickup',
    allowedInDeliveryService: 'Delivery',
    allowedInDeliService: 'Deli',
    allowedInPosService: 'POS',
    allowedInSelfCheckoutService: 'Self-checkout',

    rules: 'Rules',
    isActive: 'Active',
    isActiveHint: 'An inactive option is never offered, wherever it is allowed.',
    offlineOnly: 'Offline only',
    taxExcluded: 'Tax excluded',
    allowNonFood: 'Allow non-food products',
    allowAlcohol: 'Allow alcohol',
    allowTransfers: 'Allow transfers',
    allowServiceFee: 'Can pay the service fee',
    ignoreOrderCap: 'Ignore the order cap',
    freeDelivery: 'Delivery is free with it',
    needsBillingAgreement: 'Needs a billing agreement',
    openCashDrawer: 'Open the cash drawer',
    openCashDrawerHint: 'At the till, when this option is used.',

    credentials: 'Credentials',
    publicToken: 'Public token',
    privateToken: 'Private token',
    transferToken: 'Transfer token',

    instructions: 'Instructions to the customer',
    instructionsEn: 'English',
    instructionsEs: 'Spanish',

    group_webfront: 'Web front',
    group_pos: 'POS terminal',
    group_selfCheckout: 'Self-checkout terminal',
    webfrontCash: 'Cash',
    webfrontPayPal: 'PayPal',
    webfrontPayPalBillingAgreement: 'PayPal billing agreement',
    webfrontAth: 'ATH Móvil',
    webfrontEbt: 'EBT',
    webfrontTripleS: 'Triple-S',
    webfrontHumana: 'Humana',
    webfrontMCS: 'MCS',
    webfrontMMM: 'MMM',
    posTerminalCash: 'Cash',
    posTerminalCashOffline: 'Cash (offline)',
    posTerminalCreditDebit: 'Credit and debit',
    posTerminalEbt: 'EBT',
    posTerminalEbtCash: 'EBT Cash',
    posTerminalFondo: 'Fondo',
    posTerminalUnica: 'Unica',
    selfCheckoutTerminalCreditDebit: 'Credit and debit',
    selfCheckoutTerminalEbt: 'EBT',

    pt_0: 'Stripe',
    pt_1: 'ATH Móvil',
    pt_2: 'Cash',
    pt_3: 'Other',
    pt_4: 'EBT',
    pt_5: 'PayPal',
    pt_6: 'Cheque',
    pt_7: 'ATH',
    pt_8: 'Vanilla Card',
    pt_9: 'Gift card',
    pt_10: 'MCS',
    pt_11: 'MMM',
    pt_12: 'Accepta',
    pt_13: 'Net 30',
    pt_14: 'Net 60',
    pt_15: 'Not selected',
    pt_16: 'Credit card',
    pt_17: 'Debit card',
    pt_18: 'SSS',
    pt_19: 'Humana',
    pt_20: 'BrainTree',
    pt_21: 'In store',
    pt_22: 'Prepaid',
    pt_23: 'EBT Cash',
    pt_24: 'Unica',
    pt_25: 'Fondo',
    charge_0: 'Up front',
    charge_1: 'On dispatch',
    charge_2: 'Purchase order',
    charge_3: 'App to app',
    charge_4: 'Billing agreement',
    charge_5: 'QR code',
    charge_6: 'Terminal',
  },

  searchOptimization: {
    title: 'Search optimisation',
    subtitle: 'The terms a customer can find each item by.',
    loadFailed: 'The catalogue could not be searched.',
    actionFailed: 'That did not go through. Try again.',
    saved: 'Saved.',
    searchPlaceholder: 'Search by name, brand, barcode...',
    showingOf: 'Showing {{shown}} of {{total}}',
    noResults: 'No items match this search.',
    clearFilters: 'Clear filters',
    tableView: 'List',
    gridView: 'Grid',
    searchTags: 'Search tags',
    tagged: 'Tagged',
    edit: 'Edit',
    editItem: 'Edit item',
    customSearchTag: 'Custom search terms',
    customSearchTagNote:
      'Extra words this item should be found by — brand misspellings, local names, synonyms. Separate them with spaces.',

    sortDefault: 'Default',
    sortAZ: 'A–Z',
    sortZA: 'Z–A',
    sortRankDesc: 'Rank, highest',
    sortRankAsc: 'Rank, lowest',
    sortEditedAt: 'Recently edited',
    sortTopSellingDesc: 'Top selling',
    sortTopSellingAsc: 'Slowest selling',

    filter: {
      NoPictures: 'No picture',
      HasPicture: 'Has a picture',
      HasVideo: 'Has a video',
      PackagingSensitive: 'Packaging sensitive',
      TemperatureSensitive: 'Temperature sensitive',
      AlcoholicBeverage: 'Alcohol',
      IdRequired: 'ID required',
      Ebt: 'EBT',
      NotEbt: 'Not EBT',
      Perishable: 'Perishable',
    },
  },

  modifiers: {
    requirementsTitle: 'Requirements',
    requirementsSubtitle:
      'Choices an item must be ordered with — how it is cooked, which size.',
    sidesTitle: 'Sides',
    sidesSubtitle: 'Catalogue items that can be ordered alongside a dish.',
    addonsTitle: 'Add-ons',
    addonsSubtitle: 'Extras that can be added to an item, each with its price.',

    loadFailed: 'The list could not be loaded.',
    actionFailed: 'That did not go through. Try again.',
    saved: 'Saved.',
    deleted: 'Deleted.',
    reload: 'Reload',
    createNew: 'New',
    create: 'Create',
    edit: 'Edit',
    delete: 'Delete',
    add: 'Add',
    searchPlaceholder: 'Search by name...',
    noGroups: 'Nothing here yet.',

    newGroup: 'New group',
    editGroup: 'Edit group',
    deleteGroupPrompt:
      'Delete this group and all of its options? Items using it lose the choice.',
    internalName: 'Internal name',
    internalNameHint: 'What staff search by. Customers never see it.',
    name: 'Name',
    minimum: 'Minimum',
    maximum: 'Maximum',
    pickRange: 'Pick {{min}}–{{max}}',
    amountCountsForMinMax: 'Amounts count towards the minimum and maximum',
    amountCountsForMinMaxHint:
      'With this on, an option ordered three times counts as three choices, not one.',

    options: 'Options',
    nOptions_one: '{{count}} option',
    nOptions_other: '{{count}} options',
    noOptions: 'No options yet.',
    addOption: 'Add option',
    newOption: 'New option',
    editOption: 'Edit option',
    removeOption: 'Remove option',
    moveUp: 'Move up',
    moveDown: 'Move down',
    unavailable: 'Unavailable',

    description: 'Description',
    price: 'Price',
    brand: 'Brand',
    available: 'Available',
    availableHint: 'Off hides it from customers without deleting it.',
    allowVariableAmount: 'Can be ordered more than once',
    allowVariableAmountHint: 'Lets a customer pick a quantity of this option.',
    alcoholicBeverage: 'Alcoholic beverage',
    image: 'Image',
    uploadImage: 'Upload an image',
    clearImage: 'Remove image',
    uploadFailed: 'The image could not be uploaded.',
    searchItem: 'Find the item',
    noItemsFound: 'No items match that search.',
  },

  catalog: {
    loadFailed: 'The list could not be loaded.',
    actionFailed: 'That did not go through. Try again.',
    saved: 'Saved.',
    deleted: 'Deleted.',
    reload: 'Reload',
    createNew: 'New',
    create: 'Create',
    edit: 'Edit',
    delete: 'Delete',
    logs: 'History',
    logsFailed: 'The history could not be loaded.',
    noLogs: 'Nothing has been changed yet.',
    showingOf: 'Showing {{shown}} of {{total}}',

    name: 'Name',
    ordering: 'Order',
    orderingHint: 'Lower numbers come first in the storefront menu.',
    color: 'Background',
    borderColor: 'Border',
    image: 'Image',
    imageRequirements: 'A square PNG with a transparent background works best.',
    uploadImage: 'Upload an image',
    imageUrl: 'Or paste an image URL',
    uploadFailed: 'The image could not be uploaded.',

    // Departments
    departmentsTitle: 'Departments',
    departmentsSubtitle: 'The top level of the storefront menu.',
    noDepartments: 'No departments yet.',
    newDepartment: 'New department',
    editDepartment: 'Edit department',
    deleteDepartmentPrompt:
      'Delete {{name}}? Categories under it lose their department and stop appearing in the menu until they are reassigned.',
    viewBreakdown: 'Whole menu',
    noDepartment: 'No department',

    // Categories
    categoriesTitle: 'Categories',
    categoriesSubtitle: 'The second level of the menu, each inside a department.',
    noCategories: 'No categories yet.',
    newCategory: 'New category',
    editCategory: 'Edit category',
    deleteCategoryPrompt:
      'Delete {{name}}? Items in it stay in the catalogue but stop appearing under this category.',
    searchCategories: 'Search by name or aisle...',
    department: 'Department',
    allDepartments: 'All departments',
    selectDepartment: 'Pick a department',
    aisle: 'Aisle',
    isAlcohol: 'Alcohol',
    isAlcoholHint: 'Age checks and delivery rules key off this.',
    alcohol: 'Alcohol',
  },

  bookingSetup: {
    loadFailed: 'The list could not be loaded.',
    actionFailed: 'That did not go through. Try again.',
    saved: 'Saved.',
    deleted: 'Deleted.',
    reload: 'Reload',
    createNew: 'New',
    create: 'Create',
    edit: 'Edit',
    delete: 'Delete',
    remove: 'Remove',
    name: 'Name',
    description: 'Description',

    // Locations
    locationsTitle: 'Locations',
    locationsSubtitle:
      'Where bookings happen, and the QR code people check in with.',
    noLocations: 'No locations yet.',
    newLocation: 'New location',
    editLocation: 'Edit location',
    deleteLocationPrompt:
      'Delete this location? Events already tied to it keep their booking history, but it can no longer be chosen.',
    internalName: 'Internal name',
    internalNameHint: 'What staff type. Customers never see it.',
    image: 'Image',
    uploadImage: 'Upload an image',
    imageUrl: 'Or paste an image URL',
    uploadFailed: 'The image could not be uploaded.',
    qrCode: 'QR code',

    // Hosts
    hostsTitle: 'Hosts',
    hostsSubtitle: 'The people who run time slots.',
    noHosts: 'No hosts yet.',
    newHost: 'New host',
    editHost: 'Edit host',
    deleteHostPrompt:
      'Delete this host? Time slots they already ran keep their record, but they can no longer be assigned.',
    user: 'Who',
    userNotEditable:
      'The account behind a host cannot be changed. Delete and add again instead.',
    searchUserPlaceholder: 'Search by name or email...',
    noUsersFound: 'Nobody matches that search.',

    // Service types
    serviceTypesTitle: 'Service types',
    serviceTypesSubtitle: 'What kind of thing an event is.',
    noServiceTypes: 'No service types yet.',
    newServiceType: 'New service type',
    editServiceType: 'Edit service type',
    deleteServiceTypePrompt:
      'Delete this service type? Events already using it keep it, but it can no longer be chosen.',
  },

  heatmaps: {
    salesTitle: 'Sales heatmap',
    salesSubtitle: 'When sales happen, by day and hour.',
    bookingsTitle: 'Bookings heatmap',
    bookingsSubtitle: 'When bookings happen, by day and hour.',
    loadFailed: 'The heatmap could not be loaded.',
    noData: 'Nothing happened in this range.',
    reload: 'Reload',

    from: 'From',
    to: 'To',
    eventType: 'Event type',
    any: 'Any',

    selectACell: 'Pick an hour to see what was on.',
    nothingInHour: 'Nothing was scheduled in this hour.',
  },

  timeslotHosts: {
    title: 'Time slot hosts',
    subtitle: 'Who hosted what, over a period.',
    loadFailed: 'The report could not be loaded.',
    noResults: 'Nothing was hosted in this range.',
    searchPlaceholder: 'Search by host or event...',

    from: 'From',
    to: 'To',
    groupBy: 'Group by',
    byHost: 'Host',
    byEvent: 'Event',

    nHosts_one: '{{count}} host',
    nHosts_other: '{{count}} hosts',
    nEvents_one: '{{count}} event',
    nEvents_other: '{{count}} events',
    events: 'events',
    timeSlots: 'time slots',
    booked: 'booked',
  },

  subscriptionPaymentHistory: {
    title: 'Payment history',
    subtitle: 'Every payment taken for a membership.',
    loadFailed: 'The payment history could not be loaded.',
    noResults: 'No payments match these filters.',
    searchPlaceholder: 'Search by name, email or membership...',
    showingOf: 'Showing {{shown}} of {{total}}',

    from: 'From',
    to: 'To',
    location: 'Location',
    status: 'Status',
    any: 'Any',
    pending: 'Pending',
    successful: 'Successful',
    failed: 'Failed',

    name: 'Name',
    membership: 'Membership',
    period: 'Period paid for',
    paidOn: 'Paid on',
    amount: 'Amount',
  },

  subscriptionSales: {
    title: 'Sales',
    unredeemedGiftsTooltip:
      'Gifts that were paid for and never attached to anybody. Revenue already collected, against a membership nobody is using yet.',
    notRedeemed: 'Not redeemed',
    boughtBy: 'Bought by',
    openInGifts: 'Open in Membership gifts',
    membership: 'Membership',
    memberships: 'Memberships',
    benefits: 'Benefits',
    bookings: 'Bookings',
    showingOf: 'Showing {{shown}} of {{total}}',
    noRows: 'Nothing behind this figure.',
    openInSubscriptions: 'Open in Subscriptions',
    subtitle: 'What the memberships business did, and which plans did it.',
    loadFailed: 'The report could not be loaded.',
    searchPlaceholder: 'Search by name, email or phone...',
    thisMonthNotice:
      'Counted for the current month. Active and expiring figures are as at today.',

    // Filters
    location: 'Location',
    status: 'Status',
    originLabel: 'Origin',
    paidHow: 'Payment',
    any: 'Any',
    all: 'All',
    active: 'Active',
    expiringSoon: 'Expiring soon',
    expired: 'Expired',
    cancelled: 'Cancelled',
    belowList: 'Below list price',

    // Tabs
    overview: 'Overview',

    // Who's subscribed
    whosSubscribed: "Who's subscribed",
    activeNow: 'Active now',
    ofNEver: '{{count}} ever',
    newMemberships: 'New',
    thisMonth: 'this month',
    expiringIn30Days: 'Expiring in 30 days',
    notCountedAsSales: 'not counted as sales',
    autoRenewOn: 'Auto-renew on',

    // How they got it
    howTheyGotIt: 'How they got it',
    scopeActive: 'active subscriptions',
    paidOnline: 'Paid online',
    paidOffline: 'Paid in person',
    sponsored: 'Sponsored',

    // Money
    money: 'Money',
    collected: 'Collected',
    online: 'online',
    offline: 'in person',
    refunded: 'Refunded',
    net: 'Net',
    unbilled: 'Unbilled',
    unbilledTooltip:
      'List price of subscriptions with no payment recorded and no sponsorship. Not revenue — money that was never billed.',
    nWithNoPayment_one: '{{count}} with no payment',
    nWithNoPayment_other: '{{count}} with no payment',
    discountsGiven: 'Discounts given',
    discountsGivenTooltip:
      'How much less than list price was collected, across subscriptions that were paid. Informational — a discount, not an amount owed.',
    unredeemedGifts: 'Unredeemed gifts',

    // Plans tab
    noPlans: 'No plans sold in this period.',
    total: 'Total',
    sold: 'Sold',
    activeColumn: 'Active',
    unbilledValue: 'Unbilled value',
    soldOfLimit: '{{sold}} of {{limit}} sold',
  },

  sales: {
    title: 'Sales',
    showingOf: 'Showing {{shown}} of {{total}}',
    noRows: 'Nothing behind this figure.',
    openInBookings: 'Open in Bookings',
    subtitle:
      'What the booking business did over a period, and which events it did it at.',
    loadFailed: 'The report could not be loaded.',
    actionFailed: 'That did not go through. Try again.',
    export: 'Export',
    exportFailed: 'The export could not be generated.',
    nothingToExport: 'There is nothing to export.',
    searchPlaceholder: 'Search by name, email or event...',

    // Filters
    from: 'From',
    to: 'To',
    location: 'Location',
    eventType: 'Event type',
    serviceType: 'Service type',
    host: 'Host',
    any: 'Any',

    // Tabs
    overview: 'Overview',
    events: 'Events',

    // Attendance
    attendanceGroup: 'Attendance',
    bookings: 'Bookings',
    confirmed: 'Confirmed',
    pending: 'Pending',
    cancelled: 'Cancelled',
    checkedIn: 'Checked in',
    attendance: 'attendance',
    noShow: 'No-show',
    noShowTooltip:
      'Counted over bookings whose timeslot has already started, so checked-in plus no-show is the past, not the whole range.',
    fillRate: 'Fill rate',
    nOfMSlots: '{{bookings}} of {{slots}} slots',

    // How it was paid
    howItWasPaidGroup: 'How it was paid',
    paidOnline: 'Paid online',
    paidOffline: 'Paid in person',
    usedABenefit: 'Used a benefit',
    membershipPerks: 'membership perks',
    freeEvent: 'Free event',
    comped: 'Comped',
    compedTooltip:
      'Given away on purpose by somebody who signed for it. Not the same as a booking whose money cannot be accounted for.',
    awaitingPayment: 'Awaiting payment',
    needsReview: 'Needs review',
    needsReviewTooltip:
      'The only figure here that means something is wrong. Nobody decided to give these away — the money simply cannot be accounted for. Opens Payment review.',

    // Money
    money: 'Money',
    collected: 'Collected',
    online: 'online',
    offline: 'in person',
    refunded: 'Refunded',
    net: 'Net',
    averageTicket: 'Average ticket',
    benefitValueGiven: 'Benefit value given',
    benefitValueGivenTooltip:
      'List price of every booking paid with a membership benefit — what the memberships gave away. Never money collected.',
    nBookingsAtList_one: '{{count}} booking at list price',
    nBookingsAtList_other: '{{count}} bookings at list price',
    outstanding: 'Outstanding',

    // Events tab
    noEvents: 'No events in this range.',
    total: 'Total',
    inBenefits: 'in benefits',
    nTimeslots_one: '{{count}} time slot',
    nTimeslots_other: '{{count}} time slots',
    noTimeslots: 'No time slots in this range.',
    timeslotsFailed: 'The time slots could not be loaded.',
    nSpotsLeft_one: '{{count}} spot left',
    nSpotsLeft_other: '{{count}} spots left',
    full: 'Full',
    recordPayment: 'Record payment',
    view: 'View',

    // Add booking
    addBooking: 'Add booking',
    bookingAdded: 'The booking was added.',
    timeslotFullWarning:
      'This time slot is full. Adding somebody puts it over capacity.',
    user: 'Who',
    searchUserPlaceholder: 'Search by name, email or phone...',
    noUsersFound: 'Nobody matches that search.',
    remove: 'Remove',
    howItIsPaid: 'How it is paid',
    modeCharge: 'Charge normally',
    modeChargeHelp: 'Puts it through the usual payment flow.',
    modeNoCharge: 'No charge',
    modeNoChargeHelp: 'Books them in with nothing collected. Shows as comped.',
    modePaidOffline: 'Paid in person',
    modePaidOfflineHelp: 'Records the money along with the booking.',
    amount: 'Amount',
    method: 'Method',
    reference: 'Reference',
    adminNotes: 'Notes',
    comments: 'Comments',
    echoCharge: '{{name}} is booked in and charged as usual.',
    echoNoCharge: '{{name}} is booked in free of charge, recorded as comped.',
    echoPaidOffline: '{{name}} is booked in and {{amount}} {{method}} is recorded.',
    package: 'Package',

  },

  dashboard: {
    globalTitle: 'Global dashboard',
    globalDescription: 'Sales across every store, with a filter for one or a few.',
    storeTitle: 'Store dashboard',
    storeDescription: 'Sales for this store.',
    noStoreSelectedTitle: 'Pick a store',
    noStoreSelectedBody:
      'The store dashboard scopes to one store — choose one from the switcher to see its numbers.',
    refresh: 'Refresh',
    noData: 'No data for this range.',

    // Filters
    from: 'From',
    to: 'To',
    stores: 'Stores',
    filterByStore: 'Filter by store',
    allStores: 'All stores',
    oneStore: '1 store',
    nStores: '{{count}} stores',
    active: 'Active',
    noStores: 'No stores assigned.',
    comparedTo: 'Compared to',

    // Revenue stats
    revenue: 'Revenue',
    orders: 'Orders',
    costOfGoods: 'Cost of Goods',
    grossSales: 'Gross Sales',
    netSales: 'Net Sales',
    averageTicket: 'Average Ticket',
    amountCollected: 'Collected',

    dailyRevenue: 'Daily revenue',
    dailyRevenueHelp:
      'Net sales per day, against the matching day of the previous period.',
    dailyDetailsTitle: 'Revenue by day',
    date: 'Date',
    vs: 'vs',
    thisPeriod: 'This period',
    previousPeriod: 'Previous period',

    // Quick view / detail sheet
    participation: 'Participation',
    shareOf: 'Share of {{value}}',
    viewDetails: 'View details',
    searchProducts: 'Search by name or UPC…',

    // Payment participation
    paymentParticipation: 'Payment participation',
    paymentParticipationHelp:
      'Top five by amount collected, as a share of everything collected this period.',
    tenderType: 'Tender type',
    cardType: 'Card type',
    refundTotal: 'Refund total',
    salesTotal: 'Sales total',

    // Where it sold
    merchandise: 'Merchandise',
    merchandiseHelp:
      'Price × quantity of the catalogue products sold, before tax and discounts. Counts products paid for with a gift card — they still left the shelf. Leaves out gift card purchases, paid modifiers and lines not in the catalogue. Matches “Where it sold” and the Movement report exactly.',
    grossSalesHelp:
      'What was sold before discounts and refunds: Collected less its tax, with POS and coupon discounts added back. Built from order totals, so it counts gift card purchases and paid modifiers, and leaves out the part of a sale paid with a gift card. That is why it never ties exactly to Merchandise.',
    netSalesHelp:
      'Gross Sales less discounts and refunds, with each refund’s own tax left out so this stays a before-tax figure. Counts gift card purchases and paid modifiers; leaves out the part of a sale paid with a gift card.',
    amountCollectedHelp:
      'Money actually taken at checkout — cash, card and ATH, tax included, after POS discounts. Counts gift card purchases. Leaves out the part of a sale paid with a gift card, since no money changed hands — that is Gift card redemptions — and is counted before any later refund.',
    giftCardRedemptions: 'Gift card redemptions',
    giftCardRedemptionsHelp:
      'Paid for with gift cards on this period’s orders, tax included. No money was collected for this part of a sale, so it is not in Collected, Net Sales or Gross Sales; the products it paid for are in Merchandise. Gift card purchases are not here — they are in Collected.',
    ordersHelp:
      'Completed orders — picked up or delivered. Counts gift card purchases and orders paid entirely with a gift card. The Movement report counts only orders with a catalogue product, so it can be a little lower.',
    averageTicketHelp:
      'Collected divided by Orders. An order paid entirely with a gift card counts as an order but adds nothing to Collected, so it pulls the average down.',
    costOfGoodsHelp:
      'What the products sold cost the store: each line’s unit cost × quantity, over the same lines as Merchandise. Counts products paid for with a gift card; leaves out gift card purchases and modifiers. Merchandise less this is the profit shown in “Where it sold”.',
    groupingTotal: 'Merchandise across all rows: {{value}}',
    whereItSold: 'Where it sold',
    whereItSoldHelp:
      'Top five by sales total, as a share of all sales this period.',
    division: 'Division',
    productGroup: 'Product group',
    department: 'Department',
    category: 'Category',
    sold: 'Sold',
    profit: 'Profit',

    topProducts: 'Top products',
    topProductsHelp:
      'Top five by sales total, as a share of all product sales this period.',
    product: 'Product',
    upc: 'UPC',

    loss: 'Loss',
    lossHelp:
      'Units written off, by reason, as a share of all units written off this period.',
    otherCount_one: 'Other ({{count}})',
    otherCount_other: 'Other ({{count}})',
    ofValue: 'of {{value}}',
    composition: 'Composition',
    searchingAll: 'Searching every product sold in this period, not just the top sellers.',
    sortBy: 'Sort by',
    sortAsc: 'Sort ascending',
    sortDesc: 'Sort descending',
    reason: 'Reason',
    units: 'Units',
    // InventoryMovementReason, which the loss endpoint sends as an integer.
    reasonNone: 'Unspecified',
    reasonDamage: 'Damage',
    reasonLost: 'Lost',
    reasonFound: 'Found',
    reasonReplenishment: 'Replenishment',
    reasonSampling: 'Sampling',
    reasonGift: 'Gift',
    reasonSpoiled: 'Spoiled',
    reasonTransferToKitchen: 'Transfer to kitchen',
    reasonTransferToOtherStore: 'Transfer to another store',
    reasonTransferToDeli: 'Transfer to deli',
    reasonUnknown: 'Other',

    errorTitle: 'These numbers could not be loaded',
    errorBody:
      'The sales reports are limited to finance and administrator accounts. If yours is one of them, try again — the range may simply have been too large.',

    name: 'Name',
  },

  overview: {
    title: 'Business overview',
    subtitle: 'Memberships, bookings and gifts, in one place.',
    loadFailed: 'The overview could not be loaded.',
    noData: 'No data for this range.',

    // Filters
    from: 'From',
    to: 'To',
    location: 'Location',
    any: 'Any',
    noLocation: 'No location',
    showing: 'Showing',
    summaryAt: 'at',
    summaryEveryLocation: 'at every location',
    summaryNoLocation: 'for memberships with no location set',

    // Tabs
    money: 'Money',
    activity: 'Activity',
    people: 'People',

    moneyBasisNotice:
      'Every figure here is counted on the day the money arrived, in full. A membership paid on 20 August for a month running to 20 September counts entirely as August — it is not split across the two months. The same applies to a seat bought in July for an August class: July income here, August attendance under Bookings, so the two pages will not match for the same month.',
    activityBasisNotice:
      'Everything below is counted on the day of the class, not the day the money arrived — attendance and popularity are questions about when the class ran.',
    excludedNoLocation:
      '{{amount}} is not shown here: it came from membership plans that are not tied to any location, so it appears under no location at all. Clear the location filter to include it.',
    noLocationBookingsNotice:
      'Bookings are not shown here. Every event belongs to a location, so "no location" can only ever mean memberships and gifts.',

    // Revenue
    revenue: 'Revenue',
    netRevenue: 'Net revenue',
    netRevenueTooltip:
      'Everything received in this period, less refunds. This is what reached the bank.',
    collected: 'Collected',
    paidOnline: 'Paid online',
    paidOffline: 'Paid in person',
    owedToYou: 'Owed to you',
    owedToYouTooltip:
      'Money expected and not received, from two places. The bookings half is unpaid balances on live bookings as at today — it is not filtered by the date range, so it does not change when you look at another month. The memberships half is the list price of active memberships that were never billed at all. Neither is revenue, and because they are counted differently the total will not reconcile against any other report — chase the two halves, not the sum.',
    averagePayment: 'Average payment',
    payments: 'Payments',
    refunded: 'Refunded',
    noPaymentRecorded: 'No payment recorded',
    noPaymentRecordedTooltip:
      'Bookings an employee assigned without taking payment. They have no payment of any kind against them — not pending, not failed — so they appear in no other figure on this page. "Comped" was a decision somebody made and signed for; "needs review" is a paid seat with no payment and nobody accountable, which is worth looking into.',
    comped: 'Comped',
    needsReview: 'Needs review',
    value: 'Value',

    // By source
    revenueBySource: 'Revenue by source',
    memberships: 'Memberships',
    bookings: 'Bookings',
    gifts: 'Gifts',
    vouchers: 'Gift vouchers',
    other: 'Other',
    nothingPreviousPeriod: 'nothing in the previous period',
    previousPeriod: 'Previous period',
    whereItCameFrom: 'Where it came from',
    whereItCameFromHelp: 'Each source, with the events or plans behind it.',
    howItArrived: 'How it arrived',
    net: 'Net',
    share: 'Share',
    shareOfSource: 'Share of this source',
    nItems_one: '{{count}} item',
    nItems_other: '{{count}} items',
    offline: 'In person',
    renewal: 'Renewal',

    // Not income
    notIncome: 'Not income',
    benefitValueGiven: 'Benefit value given',
    benefitValueGivenTooltip:
      'List price of every booking paid with a membership benefit — what the memberships gave away. Never money collected.',
    voided: 'Voided',
    voidedTooltip:
      'Offline payments keyed in by mistake and later voided. Excluded from every other figure on this page.',

    // Recurring
    recurringVsOneOff: 'Recurring vs one-off',
    recurringRevenue: 'Recurring revenue',
    recurringRevenueTooltip:
      'Membership renewals. Worth separating because recurring revenue is a forecast, while one-off revenue is a result.',
    ofCollected: 'of collected',
    oneOffRevenue: 'One-off revenue',
    autoRenewing: 'Auto-renewing',
    autoRenewingTooltip:
      'Active memberships set to renew. Not revenue — a count of what is expected to bill again.',
    expectedToBillAgain: 'expected to bill again',
    dailyRevenue: 'Daily revenue',

    // Activity
    activeMemberships: 'Active memberships',
    newInPeriod: 'new in period',
    expiringSoon: 'Expiring soon',
    expiringSoonTooltip:
      'Active memberships ending within 7 days. The retention list — there is still time to do something about these.',
    recentlyExpired: 'Recently expired',
    recentlyExpiredSub: 'last 14 days, not renewed',
    recentlyExpiredTooltip:
      'Memberships that ended in the last 14 days and were not renewed, held by someone with no other active membership. The win-back list. Today’s position — the dates above do not change it.',
    events: 'Events',
    checkInRate: 'Check-in rate',
    checkInRateTooltip:
      'Check-ins over bookings for classes that have already run. Future bookings are excluded, or the current month would always look like mass absenteeism.',
    checkedIn: 'Checked in',
    usedABenefit: 'Used a benefit',
    uniqueCustomers: 'Unique customers',
    plansSelling: 'Plans selling',
    activeNow: 'Active now',
    purchases: 'Purchases',
    new: 'New',
    renewals: 'Renewals',
    topEvents: 'Top events',
    topEventsHelp:
      'Ranked by how many people booked. Benefit bookings are shown separately — a class full of members is popular and earns nothing directly.',
    fillRate: 'Fill rate',

    // People
    topBenefitUsers: 'Top benefit users',
    topBenefitUsersHelp:
      'Your happiest members — the ones whose membership is clearly worth it to them.',
    unusedBenefits: 'Unused benefits',
    unusedBenefitsHelp:
      'Active memberships with no benefits used in this period, soonest to expire first. Not bad customers — customers about to cancel.',
    mostConsistent: 'Most consistent',
    mostConsistentHelp:
      'Highest check-in rate, among people with at least a few bookings so one lucky attendance cannot top the list.',
    paidButNoShow: 'Paid but no-show',
    paidButNoShowHelp:
      'Spent money on bookings and attended none of them. A refund request waiting to happen.',
    dropInsToConvert: 'Drop-ins to convert',
    dropInsToConvertHelp:
      'Training and paying per class with no membership anywhere. Ranked by how much they already spend against the cheapest plan you could sell them — anyone above it is paying more than a membership would cost.',
    convertedCustomers: 'Converted customers',
    convertedCustomersHelp:
      'Members who used to pay per class, bought a membership, and kept coming back on benefits. The date range scopes their activity, not who is on the list — a conversion happened once, whenever it happened.',
    exportList: 'Export list',
    benefitsUsed: 'Benefits used',
    expires: 'Expires',
    spent: 'Spent',
    spentVsPlan: 'Spent vs plan',
    spentBeforeJoining: 'Spent before joining',
    bookingsBeforeJoining: 'Bookings before joining',
    planPriceCompare: '{{plan}} is {{price}}',
    convertedOn: 'Converted {{date}}',
    converted: 'Converted',
    benefitsSinceJoining_one: '{{count}} benefit used since',
    benefitsSinceJoining_other: '{{count}} benefits used since',
    minBookingsNote:
      'Only people with at least {{count}} bookings on classes that have already run are ranked, so a single lucky attendance cannot top the list.',
    lapsed: 'Lapsed',
    neverAMember: 'Never a member',
    plan: 'Plan',
    name: 'Name',
    email: 'Email',
    phone: 'Phone',

    // Drill-down
    total: 'Total',
    nRows_one: '{{count}} row',
    nRows_other: '{{count}} rows',
  },

  paymentReview: {
    title: 'Payment review',
    subtitle:
      'Bookings that should have been paid for and have no payment recorded. Each one ends in a decision.',
    loadFailed: 'The review list could not be loaded.',
    actionFailed: 'That did not go through. Try again.',
    refresh: 'Refresh',

    // Header
    lastSearchedAt: 'PayPal last searched {{when}}.',
    neverSearched: 'PayPal has not been searched for these yet.',
    searchPaypalNow: 'Search PayPal now',
    findOldPayments: 'Find old payments',
    sweepFinished:
      'Searched {{searched}} booking(s) and found {{found}} possible payment(s).',

    // Summary
    openReviews: 'Waiting on a decision',
    amountAtStake: 'Amount at stake',
    allClear: 'Every booking has its payment accounted for.',

    // The two halves of the list
    found: 'Possible payments found',
    foundHelp:
      'These bookings have no payment recorded, but a PayPal payment appears to match. Check the payer and amount, then attach the right one.',
    noMatch: 'No payment found',
    noMatchHelp:
      'Nothing in PayPal matches these bookings. Confirm each one was given away on purpose, or cancel it.',

    // Booking facts
    bookedAt: 'Booked',
    confirmed: 'Confirmed',
    pending: 'Pending',
    cancelled: 'Cancelled',

    // Candidates
    needsAChoice: 'Needs a choice',
    readyToAttach: 'Ready to attach',
    ruledOutCount_one: '{{count}} payment already ruled out',
    ruledOutCount_other: '{{count}} payments already ruled out',
    alsoFitsOtherBookings: 'Also fits another booking',
    alsoFitsOtherBookingsHelp:
      'This same payment also matches a different booking. Attaching it here means it is no longer available for that one.',
    attachPayment: 'Attach',
    attachPaymentPrompt:
      "Record this {{amount}} PayPal payment against {{name}}'s booking?",
    paymentAttached: 'The payment was attached.',
    notThisOne: 'Not this one',
    rejectPaymentPrompt:
      'Rule this payment out for this booking? It will not be suggested again.',
    paymentRuledOut: 'That payment will not be suggested again.',

    // No-match decisions
    awaitingPayment: 'Awaiting payment',
    needsReview: 'Needs review',
    searchedTimes_one: 'Searched {{count}} time',
    searchedTimes_other: 'Searched {{count}} times',
    confirmComped: 'Confirm as comped',
    confirmCompedHelp:
      "This records that {{name}}'s booking for {{event}} was given away on purpose and no payment is missing. Add a reason so the next person understands why.",
    compedReasonPlaceholder: 'Why was this given away?',
    comped: 'Recorded as comped.',
    cancelBooking: 'Cancel booking',
    cancelBookingPrompt:
      "Cancel {{name}}'s booking? They will be told and the spot is released.",
    bookingCancelled: 'The booking was cancelled.',

    // Find old payments
    close: 'Close',
    preview: 'Preview',
    applyChanges: 'Apply',
    findOldPaymentsHelp:
      "Searches for payments belonging to this year's bookings that have none recorded. It links memberships whose benefit was never attached, then applies any PayPal payment it can identify by the payment's own reference or by payer email, amount and date. Preview first; nothing is written until you apply.",
    reconcileApplyPrompt:
      'Apply these changes? Payments will be recorded against the bookings shown.',
    previewOnly: 'This was a preview. Nothing has been written yet.',
    bookingsExamined: 'examined',
    paypalPaymentsScanned: 'PayPal payments scanned',
    alreadyRecordedElsewhere:
      'of those are already recorded on another booking or membership, and were not offered',
    benefitsRelinked: 'were membership benefits',
    matchedByReference: 'matched by payment reference',
    matchedByEvidence: 'matched by email, amount and date',

    // Why a match failed
    whyNoMatch_one: 'Why {{count}} found nothing',
    whyNoMatch_other: 'Why {{count}} found nothing',
    missNoPaymentFromPayer:
      'have no PayPal payment from that email address at all — they paid another way, or their PayPal account uses a different address',
    missWrongAmount:
      "did pay by PayPal, but never for this booking's price — usually a price that changed since",
    missWrongPayer:
      'have a payment for the right amount at the right time from a different email address',
    missOutsideWindow:
      'have the right payer and amount, but further apart than the {{hours}}-hour window',
    missClosestGap: '(closest was {{hours}} hours)',

    // Outcomes
    outcomeBenefit: 'Membership benefit',
    outcomeCustomId: 'Matched by reference',
    outcomeEvidence: 'Matched by evidence',
    outcomeNone: 'Nothing found',

    // Closing out the old backlog
    olderBookings: 'Older bookings',
    olderBookingsHelp:
      'Bookings made before {{date}} are too old to search PayPal for. Closing them out takes them off this list and out of the Needs review count. They are recorded as closed without review — not as comps, because nobody checked them.',
    closeOutPreview_one: '{{count}} booking worth {{amount}} would be closed out.',
    closeOutPreview_other: '{{count}} bookings worth {{amount}} would be closed out.',
    closeOutApplyPrompt_one:
      'Close out {{count}} booking without searching for its payment?',
    closeOutApplyPrompt_other:
      'Close out {{count}} bookings without searching for their payments?',
    closeThemOut: 'Close them out',
    archivedBookings_one: '{{count}} older booking closed out.',
    archivedBookings_other: '{{count}} older bookings closed out.',
  },

  /**
   * Gift cards. One block for what used to be two pages — an Admin Services
   * one and a Finance report one — over the same endpoints.
   */
  giftCards: {
    title: 'Gift Cards',
    subtitle:
      'Card balances, the transactions behind them, and what is still owed on unspent cards.',

    accountPayable: 'Outstanding balance',
    accountPayableHint: 'Unspent value on activated cards — what the business still owes.',
    activeCards: 'Active cards',
    activeCardsHint: 'Cards activated and not yet emptied.',
    payableFailed: 'The outstanding balance could not be loaded.',

    tabCards: 'Cards',
    tabTransactions: 'Transactions',
    tabPurchases: 'Purchases',

    from: 'From',
    to: 'To',
    allStores: 'All stores',
    showingOf: 'Showing {{shown}} of {{total}}',
    periodTotals:
      'Loaded {{purchased}} and redeemed {{redeemed}} over the whole period, not only the rows on this page.',
    loadFailed: 'That list could not be loaded.',

    searchPlaceholder: 'Search by code or nickname…',
    cardState: 'Card state',
    stateAll: 'All',
    stateActivated: 'Activated',
    stateWithBalance: 'Activated, with balance',
    stateNoBalance: 'Activated, empty',
    stateNotActivated: 'Not activated',

    noCards: 'No gift cards match this search.',
    noTransactions: 'No transactions in this period.',
    noPurchases: 'No purchases in this period.',

    code: 'Code',
    owner: 'Owner',
    created: 'Created',
    activated: 'Activated',
    activatedBadge: 'Activated',
    activatedOn: 'Activated {{date}}',
    notActivated: 'Not activated',
    opening: 'Opening',
    balance: 'Balance',
    balanceAfter: 'Balance after',
    unassigned: 'Nobody',
    view: 'View',

    when: 'When',
    order: 'Order',
    store: 'Store',
    type: 'Type',
    amount: 'Amount',
    load: 'Load',
    redemption: 'Redemption',

    revert: 'Revert',
    revertConfirm:
      'This undoes {{amount}} on card {{code}} and puts the balance back. It cannot be redone from here.',
    reverted: 'Transaction reverted.',
    actionFailed: 'That action could not be completed.',

    cardLoadFailed: 'This card could not be loaded.',
    nickname: 'Nickname',
    nicknamePlaceholder: 'A name to recognise this card by',
    nicknameSaved: 'Nickname saved.',

    assignOwner: 'Assign owner',
    removeOwner: 'Remove',
    ownerAssigned: 'Owner assigned.',
    ownerRemoved: 'Owner removed.',
    searchUser: 'Search by name, email or phone…',
    noUsersFound: 'No users found.',

    history: 'History',
    recalculate: 'Recalculate',
    balanceRecalculated: 'Balance recalculated from the transactions.',
    noCardHistory: 'Nothing has moved on this card yet.',

    createCards: 'Create cards',
    create: 'Create',
    createBody:
      'Blank codes are generated and downloaded as a text file. That file is the only copy — the codes cannot be listed again as a batch afterwards.',
    howMany: 'How many',
    batchLimit: 'Between 1 and {{max}} at a time.',
    created_one: '{{count}} code created and downloaded.',
    created_other: '{{count}} codes created and downloaded.',
  },

  /** Shared by every image picker; the field is used across Marketing. */
  imageUpload: {
    prompt: 'Click or drop an image here',
    urlPlaceholder: 'https://… or upload below',
    replace: 'Replace',
    clear: 'Remove',
    wrongType: 'That file is not a JPEG, PNG, WebP or GIF.',
    tooLarge: 'That image is over 5 MB. Compress it and try again.',
    failed: 'The image could not be uploaded.',
  },

  infoCards: {
    title: 'Info Cards',
    subtitle:
      'The promotional panels the app and web front show, and when each one runs.',
    searchPlaceholder: 'Search by name or title…',
    showingOf: 'Showing {{shown}} of {{total}}',
    loadFailed: 'The info cards could not be loaded.',
    none: 'No info cards yet.',
    noneMatch: 'No info cards match these filters.',

    status: 'Status',
    statusAny: 'Any status',
    statusLive: 'Live',
    statusPlanned: 'Planned',
    statusCompleted: 'Completed',
    statusInactive: 'Inactive',
    daysLeft_one: '{{count}} day left',
    daysLeft_other: '{{count}} days left',

    name: 'Name',
    nameHint: 'Internal only — how you will recognise this card in the list.',
    store: 'Store',
    storeHint: 'Leave on all stores unless this card is for one of them.',
    allStores: 'All stores',
    oneStore: 'One store',
    window: 'Window',
    shownIn: 'Shown in',
    app: 'App',
    web: 'Web',
    nowhere: 'Nowhere',
    edit: 'Edit',

    newCard: 'New card',
    editCard: 'Edit info card',
    create: 'Create',
    created: 'Info card created.',
    updated: 'Info card saved.',
    saveFailed: 'The info card could not be saved.',
    archive: 'Archive',
    archiveConfirm:
      '"{{name}}" is removed from the list and stops showing anywhere. This cannot be undone from here.',
    archived: 'Info card archived.',

    content: 'Content',
    cardTitle: 'Title',
    body: 'Body',
    htmlBody: 'HTML body',
    htmlBodyHint: 'Optional. Used by clients that render rich text instead of the plain body.',
    actionText: 'Button text',
    actionUrl: 'Button link',
    actionUrlHint: 'Where the card sends someone who taps it. Shared by both languages.',
    image: 'Image',
    imageHint: 'Shown to readers in this language.',

    schedule: 'Schedule',
    starts: 'Starts',
    ends: 'Ends',
    endBeforeStart: 'The end has to come after the start.',

    placement: 'Placement',
    isActive: 'Active',
    isActiveHint: 'Off hides the card everywhere, whatever its dates say.',
    showInApp: 'Show in app',
    showInWeb: 'Show in web',
    ordering: 'Order',
    orderingHint: 'Lower numbers come first.',
    defaultHeight: 'Default height',
    height: 'Height (px)',

    targeting: 'Targeting',
    gender: 'Gender',
    genderAny: 'Anyone',
    genderMale: 'Male',
    genderFemale: 'Female',
    ageRange: 'Age group',
    ageAll: 'All ages',
    ageMinor: 'Minors',
    ageTeenager: 'Teenagers',
    ageMiddleAged: 'Middle aged',
    ageSenior: 'Seniors',
  },

  bannerAds: {
    title: 'Banner Ads',
    subtitle:
      'The promotional strips the app and web front carry, and whether each one is approved to run.',
    searchPlaceholder: 'Search by name or store…',
    showingOf: 'Showing {{shown}} of {{total}}',
    loadFailed: 'The banner ads could not be loaded.',
    none: 'No banner ads yet.',
    noneMatch: 'No banner ads match these filters.',

    status: 'Status',
    statusAny: 'Any status',
    statusLive: 'Live',
    statusScheduled: 'Scheduled',
    statusEnded: 'Ended',
    statusUnapproved: 'Not approved',

    name: 'Name',
    nameHint: 'Internal only — how you will recognise this banner in the list.',
    position: 'Position',
    anyPosition: 'Any position',
    placementTop: 'Top',
    placementBottom: 'Bottom',
    placementLeft: 'Left',
    placementRight: 'Right',
    placementPopup: 'Popup',
    window: 'Window',
    shownIn: 'Shown in',
    app: 'App',
    web: 'Web',
    nowhere: 'Nowhere',
    store: 'Store',
    storeHint: 'Leave on all stores unless this banner is for one of them.',
    allStores: 'All stores',
    edit: 'Edit',

    newBanner: 'New banner',
    editBanner: 'Edit banner',
    create: 'Create',
    created: 'Banner created.',
    updated: 'Banner saved.',
    saveFailed: 'The banner could not be saved.',
    archive: 'Archive',
    archiveConfirm:
      '"{{name}}" stops running and leaves this list. The record is kept, but the portal cannot bring it back.',
    archived: 'Banner archived.',

    media: 'Media',
    imageEnglish: 'English image',
    imageSpanish: 'Spanish image',
    imageSpanishHint: 'Optional. English is used for Spanish readers when this is empty.',
    backgroundColor: 'Background colour',
    backgroundColorHint: 'Shows behind the image where it does not fill the space.',

    schedule: 'Schedule',
    starts: 'Starts',
    ends: 'Ends',
    datesHint: 'Leave either empty to run open-ended in that direction.',
    endBeforeStart: 'The end has to come after the start.',

    placement: 'Placement',
    approved: 'Approved',
    approvedHint: 'Nothing runs until this is on, whatever the dates say.',
    showInApp: 'Show in app',
    showInWeb: 'Show in web',
    ordering: 'Order',
    orderingHint: 'Lower numbers come first among banners in the same position.',

    action: 'Action',
    actionType: 'On tap',
    actionNone: 'Do nothing',
    actionLink: 'Open a link',
    actionSearch: 'Run a search',
    linkUrl: 'Link',
    linkUrlHint: 'Where the banner sends someone who taps it.',
    searchTerm: 'Search term',
    searchTermHint: 'The catalogue search this banner runs when tapped.',
  },

  promoCodes: {
    title: 'Promo Codes',
    subtitle:
      'Discount codes anyone can type at checkout, and codes issued to one customer.',

    tabStore: 'Store codes',
    tabCustomer: 'Customer codes',

    searchStore: 'Search by code or store…',
    searchCustomerCodes: 'Search by code or customer…',
    searchLabel: 'Search promo codes',
    showingOf: 'Showing {{shown}} of {{total}}',
    loadFailed: 'The promo codes could not be loaded.',
    none: 'No promo codes match this search.',
    allStores: 'All stores',

    code: 'Code',
    codeHint: 'What the customer types. Uppercase, and it cannot be changed after saving.',
    theCode: 'The code',
    customer: 'Customer',
    store: 'Store',
    global: 'All stores',
    value: 'Value',
    minPurchase: 'Minimum purchase',
    minPurchaseHint: 'Leave at 0 for no minimum.',
    noMinimum: 'None',
    expires: 'Expires',
    expiryHint: 'Valid through the end of this day. Leave empty and it never expires.',
    neverExpires: 'Never expires',
    expired: 'Expired',

    scope: 'Where it applies',
    storeHint: 'Leave on all stores unless this code is for one of them.',
    appliesTo: 'Applies to',
    appliesTo0: 'Pickup',
    appliesTo1: 'Delivery',
    appliesTo2: 'Pickup and delivery',
    usageLimit: 'Usage limit',
    usageLimitHint: 'How many times this one code can be redeemed.',
    usage0: 'Once in total',
    usage1: 'Unlimited',
    usage2: 'Once per customer',

    notification: 'Notification',
    notifyCustomer: 'Tell the customer',
    notifyHint: 'Sends the code to them with the message below. Off issues it silently.',
    message: 'Message',
    searchCustomer: 'Search by name, email or phone…',
    noCustomers: 'No customers found.',
    clearCustomer: 'Choose someone else',

    newStoreCode: 'New store code',
    newCustomerCode: 'New customer code',
    editCode: 'Edit promo code',
    editExpiry: 'Edit expiry',
    create: 'Create',
    created: 'Promo code created.',
    expiryUpdated: 'Expiry updated.',
    saveFailed: 'The promo code could not be saved.',
    expiryOnlyNotice:
      'Only the expiry date can be changed. Everything else is fixed once a code is created — archive this one and make a new one to change it.',

    archive: 'Archive',
    archiveConfirm:
      '"{{code}}" stops working immediately and leaves this list. Redemptions already made are kept.',
    archived: 'Promo code archived.',

    redemptions: 'Redemptions',
    redemptionsFor: 'Every order that has used {{code}}.',
    redemptionsFailed: 'The redemptions could not be loaded.',
    neverRedeemed: 'This code has not been used yet.',
    close: 'Close',
  },

  sponsorships: {
    title: 'Sponsorships',
    subtitle:
      'A window, a set of items and a set of stores. Nothing reaches the catalogue until you apply it.',
    searchPlaceholder: 'Search by name…',
    showingOf: 'Showing {{shown}} of {{total}}',
    loadFailed: 'The sponsorships could not be loaded.',
    detailFailed: 'This sponsorship could not be loaded.',
    none: 'No sponsorships yet.',
    noneMatch: 'No sponsorships match these filters.',
    backToList: 'All sponsorships',
    open: 'Open',
    edit: 'Edit',

    status: 'Status',
    statusAny: 'Any status',
    statusRunning: 'In window',
    statusScheduled: 'Scheduled',
    statusFinished: 'Finished',

    name: 'Name',
    nameHint: 'Internal only — how you will recognise this sponsorship.',
    window: 'Window',
    starts: 'Starts',
    ends: 'Ends',
    endBeforeStart: 'The end has to come after the start.',
    windowHint:
      'This window is what gets written onto each product when you apply the sponsorship. Both days are included.',

    newSponsorship: 'New sponsorship',
    editSponsorship: 'Edit sponsorship',
    create: 'Create',
    created: 'Sponsorship created.',
    updated: 'Sponsorship saved.',
    saveFailed: 'The sponsorship could not be saved.',
    delete: 'Delete',
    deleteConfirm:
      '"{{name}}" is removed along with its item and store assignments. Products already stamped by an earlier apply keep their dates.',
    deleted: 'Sponsorship deleted.',

    pushToCatalogue: 'Push to the catalogue',
    pushExplainer_zero:
      'Applying writes this window onto every product in this sponsorship, at every store in it. Add some of each first.',
    pushExplainer_one:
      'Applying writes this window onto {{count}} product-and-store pairing. Cancelling back-dates the same pairing so it stops showing as sponsored.',
    pushExplainer_other:
      'Applying writes this window onto {{count}} product-and-store pairings. Cancelling back-dates the same pairings so they stop showing as sponsored.',
    apply: 'Apply',
    applyConfirm_one:
      'Up to {{count}} pairing is updated — only stores that actually carry the product are touched. Run it again after changing the items, stores or dates.',
    applyConfirm_other:
      'Up to {{count}} pairings are updated — only stores that actually carry the product are touched. Run it again after changing the items, stores or dates.',
    applied: 'Sponsorship applied to the catalogue.',
    cancelRun: 'Cancel',
    cancelConfirm_one:
      'Up to {{count}} pairing is back-dated and stops showing as sponsored. The sponsorship itself is kept and can be applied again.',
    cancelConfirm_other:
      'Up to {{count}} pairings are back-dated and stop showing as sponsored. The sponsorship itself is kept and can be applied again.',
    cancelled: 'Sponsorship cancelled in the catalogue.',
    needBoth: 'Add at least one store and one item before applying.',
    actionFailed: 'That action could not be completed.',

    stores_one: '{{count}} store',
    stores_other: '{{count}} stores',
    chooseStore: 'Choose a store…',
    noStores: 'No stores on this sponsorship yet.',
    items_one: '{{count}} item',
    items_other: '{{count}} items',
    searchItems: 'Search the catalogue…',
    noItemsFound: 'No products matched.',
    noItems: 'No items on this sponsorship yet.',
    add: 'Add',
    added: 'Added',
    remove: 'Remove',
  },

  movement: {
    title: 'Movement',
    subtitle:
      'What sold, grouped by where the product sits. Pick a row to go deeper.',
    level: 'Group by',
    level_division: 'Division',
    level_group: 'Item group',
    level_department: 'Department',
    level_category: 'Category',
    store: 'Store',
    allStores: 'All stores',
    department: 'Department',
    pickDepartment: 'Pick a department',
    pickDepartmentFirst:
      'Categories live inside a department. Pick one to see its categories.',
    from: 'From',
    to: 'To',
    breadcrumb: 'Report level',
    viewAllItems: 'View all items',
    allItems: 'All items',
    orders: 'Orders',
    ordersHint:
      'Orders with at least one catalogue product sold. A gift card purchase has none, so it is never counted here; the dashboard’s Orders includes it.',
    lines: 'Order lines',
    linesHint:
      'Lines on an order, not distinct products — the same product bought twice counts twice.',
    unitsSold: 'Units sold',
    unitsHint:
      'Quantity sold across catalogue lines. Weighed products count their weight, so this is not always a whole number.',
    units: 'Units',
    merchandise: 'Merchandise',
    merchandiseHint:
      'Price × quantity of the catalogue products sold, before tax and discounts. Counts products paid for with a gift card — the product still moved, even though no money was collected. Never includes a gift card purchase: that is money collected, not a product. Matches the dashboard’s Merchandise and “Where it sold” exactly.',
    total: 'Total',
    totalHint:
      'Merchandise plus its tax plus paid modifiers. Not comparable to the dashboard’s Collected, which counts gift card purchases, leaves out what was paid with a gift card, and is after POS discounts.',
    totalBreakdown: 'incl. {{tax}} tax, {{modifiers}} modifiers',
    share: 'Share',
    modifiers: 'Modifiers',
    profit: 'Profit',
    margin: 'Margin',
    item: 'Item',
    upc: 'UPC',
    priceTotal: 'Price total',
    stateTax: 'State tax',
    municipalTax: 'Municipal tax',
    totalSales: 'Total sales',
    avgPrice: 'Avg price',
    avgCost: 'Avg cost',
    modifier: 'Modifier',
    modifiersOn: 'Modifiers on {{item}}',
    showModifiers: 'Show modifiers on {{item}}',
    unnamed: 'Unnamed',
    substituted: 'Substituted items',
    noRows: 'Nothing sold in this range.',
    noItems: 'No items in this selection.',
    export: 'Export',
    exported: 'Exported {{count}} rows.',
    nothingToExport: 'Nothing to export.',
    failed: 'Could not load the report.',
  },
  settlements: {
    title: 'Settlements',
    subtitle:
      'Each terminal batch as it closed. Pick a batch for its breakdown and receipt.',
    store: 'Store',
    allStores: 'All stores',
    lane: 'Lane',
    allLanes: 'All lanes',
    from: 'From',
    to: 'To',
    sales: 'Settled sales',
    salesSub: '{{transactions}} transactions in {{batches}} batches',
    salesHint:
      'Card, ATH Móvil and cash-card sales as each terminal closed its batch, tax included. Dated by when the batch closed, not by the sale — a batch closed in the morning holds the evening before. Not comparable to the dashboard’s Collected, which also counts cash in the drawer and gift card purchases, is dated by the sale, and is counted before refunds.',
    credit: 'Credit',
    creditHint: 'Visa, Mastercard, American Express and Discover.',
    debit: 'Debit',
    debitHint: 'ATH debit cards and ATH Móvil.',
    cash: 'Cash card',
    cashHint:
      'What the terminal files under “Cash” — the POS records these as CASH CARD. Not cash in the drawer, which never touches the terminal.',
    transactions: '{{transactions}} transactions',
    refunds: 'Refunds',
    refundsHint:
      'Refunds the terminal returned to a card in these batches, tax included. Voids are not here: a voided sale is taken out of its batch instead.',
    difference: 'POS difference',
    differenceHint:
      'Settled sales less what the POS recorded for the same batches, matched by the batch number the terminal stamps on every transaction. Above zero, the terminal approved money the POS never saved. A batch with no earlier settlement on its lane to start from is not compared.',
    differenceNone: 'Every batch matches the POS',
    differenceSome: '{{differing}} of {{compared}} batches differ',
    notCompared: '{{batches}} not compared',
    closedAt: 'Closed',
    transactionsColumn: 'Transactions',
    salesColumn: 'Sales',
    net: 'Net',
    posRecorded: 'POS recorded',
    differenceColumn: 'Difference',
    matches: 'Matches',
    notComparedShort: 'Not compared',
    noRows: 'No batches were closed in this range.',
    failed: 'Could not load the settlements.',
    export: 'Export',
    exported: 'Exported {{rows}} batches.',
    nothingToExport: 'Nothing to export.',
    batchTitle: '{{lane}} · {{store}}',
    laneFallback: 'Lane',
    closedOn: 'Closed {{at}}',
    openedOn: 'Opened {{at}}',
    openedUnknown: 'no earlier settlement on this lane',
    comparison: 'Compared with the POS',
    terminal: 'Terminal',
    pos: 'POS',
    saleCount: 'Sales',
    saleAmount: 'Sales amount',
    refundCount: 'Refunds',
    refundAmount: 'Refunds amount',
    explainMatch: 'The POS recorded every transaction in this batch, to the cent.',
    explainTerminalHigher:
      'The terminal closed {{amount}} more than the POS recorded for this batch — a charge the terminal approved that the POS never saved.',
    explainPosHigher:
      'The POS recorded {{amount}} more than the terminal closed for this batch.',
    explainRefunds: 'Refunds differ by {{amount}}.',
    explainNotCompared:
      'Not compared: this lane has no earlier settlement to mark where the batch began, or the batch stayed open for more than 14 days.',
    categories: 'By terminal category',
    category: 'Category',
    total: 'Total',
    terminalId: 'Terminal ID',
    batchNumber: 'Batch',
    status: 'Terminal status',
    viewReceipt: 'View receipt',
    noReceipt: 'No receipt',
    receiptTitle: 'Settlement receipt',
    receiptFailed: 'Could not load the receipt.',
    close: 'Close',
  },
  salesSummary: {
    title: 'Sales Summary',
    subtitle:
      'The dashboard’s revenue figures for any range, with the lines that add up to them — payments, taxes and cash drops.',
    store: 'Store',
    allStores: 'All stores',
    channel: 'Orders',
    channel_all: 'All orders',
    channel_pos: 'POS',
    channel_selfCheckout: 'Self-checkout',
    channel_online: 'Online',
    from: 'From',
    to: 'To',
    sameTile:
      'With All orders, this is the dashboard’s {{tile}} for the same stores and dates.',
    channelTile:
      'Limited to {{channel}} orders, so it matches the dashboard’s {{tile}} only when every order in the range was one.',
    collectedStatement: 'How Collected adds up',
    netStatement: 'How Net Sales adds up',
    discounts: 'Discounts',
    tax: 'Tax',
    taxDetail: '{{terminal}} on terminal records, {{unlogged}} on orders without one',
    refundsBeforeTax: 'Refunds, before tax',
    refundsDetail: '{{refunds}} refunded, less {{tax}} of it that was tax',
    giftCards: 'Gift cards',
    giftCardsSold: 'Gift cards sold',
    giftCardsSoldDetail: 'Already in Collected — selling one takes money.',
    giftCardRedemptionsDetail:
      'Not in Collected — paying with one takes no money.',
    payments: 'Payments',
    paymentsHelp:
      'Collected by tender, grouped as the dashboard’s Payment participation groups it.',
    paymentsTotal: 'The rows add up to Collected: {{collected}} across {{orders}} orders.',
    ordersColumn: 'Orders',
    refundsColumn: 'Refunds',
    afterRefundsColumn: 'After refunds',
    noOrders: 'No orders in this range.',
    taxes: 'Taxes',
    taxesHelp:
      'All the tax inside Collected. Terminal records split it by rate; an order rung up without the terminal carries only its own total.',
    taxColumn: 'Tax',
    applicableColumn: 'Applicable sales',
    taxCollectedColumn: 'Collected',
    taxRefundedColumn: 'Refunded',
    taxNetColumn: 'Net',
    unloggedRow: 'No terminal record',
    unloggedRowDetail: 'Cash or ATH sales rung up without the terminal',
    total: 'Total',
    drops: 'Cash drops',
    dropsHelp:
      'Cash taken out of a drawer and into the safe — the same drops the Cash Drawer report counts.',
    dropsSummary: '{{drops}} drops · {{amount}}',
    time: 'Time',
    lane: 'Lane',
    employee: 'Employee',
    amount: 'Amount',
    noDrops: 'No cash drops in this range.',
    failed: 'Could not load the summary.',
  },
  salesReport: {
    title: 'Sales',
    subtitle:
      'The dashboard’s revenue measures, split by day, lane, employee, store or tender. Pick a row for its orders.',
    store: 'Store',
    allStores: 'All stores',
    groupBy: 'Group by',
    groupBy_day: 'Day',
    groupBy_lane: 'Lane',
    groupBy_employee: 'Employee',
    groupBy_store: 'Store',
    groupBy_tenderType: 'Tender type',
    groupBy_cardType: 'Card type',
    from: 'From',
    to: 'To',
    discounts: 'Discounts',
    tax: 'Tax',
    refunds: 'Refunds',
    refundsHint:
      'Credit refunds on these orders, tax included. Net Sales takes them off without their tax.',
    noLane: 'No lane',
    noEmployee: 'No employee',
    unnamed: 'Unnamed',
    rowsTotal:
      'The rows add up to the tiles: {{collected}} collected across {{orders}} orders.',
    noRows: 'No orders in this range.',
    failed: 'Could not load the sales.',
    export: 'Export',
    exported: 'Exported {{rows}} rows.',
    nothingToExport: 'Nothing to export.',
    ordersSubtitle: '{{orders}} orders · {{collected}} collected',
    time: 'Time',
    order: 'Order',
    lane: 'Lane',
    employee: 'Employee',
    tender: 'Tender',
    noOrders: 'No orders in this group.',
    ordersFailed: 'Could not load the orders.',
    refundedAmount: '{{amount}} refunded',
    giftCardAmount: '{{amount}} by gift card',
  },
  posDiscounts: {
    title: 'POS Discounts',
    subtitle: 'What a cashier can apply at the till.',
    usageTitle: 'POS Discount Report',
    usageSubtitle:
      'Where the till discounts went, and what they cost. Read-only — the discounts themselves are maintained under Marketing.',

    searchPlaceholder: 'Search by name…',
    loadFailed: 'The POS discounts could not be loaded.',
    none: 'No POS discounts yet.',
    noneMatch: 'No discounts match this search.',

    name: 'Name',
    nameEn: 'Name (English)',
    nameEs: 'Name (Spanish)',
    discountType: 'Type',
    discountTypeHint: 'The last three waive tax rather than taking money off.',
    type0: 'Fixed amount',
    type1: 'Percentage',
    type2: 'No state tax',
    type3: 'No municipal tax',
    type4: 'No taxes',
    value: 'Value',
    amount: 'Amount off',
    percentage: 'Percent off',
    percentageHint: 'Entered and shown as a percentage — 10 means 10% off.',
    scope: 'Applies at',
    allStores: 'All stores',
    oneStore: 'One store',
    exclusions: 'Exclusions',
    exclusionCount_one: '{{count}} excluded item',
    exclusionCount_other: '{{count}} excluded items',
    status: 'Status',
    active: 'Active',
    inactive: 'Inactive',
    edit: 'Edit',

    details: 'Details',
    isActive: 'Active',
    isActiveHint: 'Off keeps the discount but hides it from every till.',
    multiple: 'Can be applied more than once',
    multipleHint: 'Lets a cashier stack this discount on the same sale.',
    multipleShort: 'Stackable',

    newDiscount: 'New discount',
    editDiscount: 'Edit discount',
    create: 'Create',
    created: 'Discount created.',
    updated: 'Discount saved.',
    saveFailed: 'The discount could not be saved.',
    delete: 'Delete',
    deleteConfirm:
      '"{{name}}" is removed and stops being offered at the till. Discounts already applied to past orders are kept.',
    deleted: 'Discount deleted.',

    excludedItems: 'Excluded items',
    excludedHint:
      'This discount applies to everything except the products listed here.',
    excludedAfterSave: 'Save the discount first, then choose what to exclude.',
    searchItems: 'Search the catalogue…',
    noItemsFound: 'No products matched.',
    noExclusions: 'Nothing is excluded — this discount applies to everything.',
    exclude: 'Exclude',
    excluded: 'Excluded',
    removeExclusion: 'Stop excluding',

    discounts: 'Discounts',
    allDiscounts: 'All discounts',
    oneDiscount: 'One discount',
    nDiscounts: '{{count}} discounts',
    store: 'Store',
    period: 'Period',
    dateRange: 'Date range',
    lifetime: 'All time',
    from: 'From',
    to: 'To',

    totalGiven: 'Total discounted',
    ordersWithDiscount: 'Orders',
    customers: 'Customers',
    orders: 'Orders',
    given: 'Discounted',
    orderTotal: 'Order total',
    noUsage: 'No discounts were applied in this period.',
    noOrders: 'No orders recorded for this discount.',
    reportFailed: 'The usage report could not be loaded.',
    reportNote: 'Covering {{scope}}. Amounts are what was taken off, not what was sold.',
  },

  loyalty: {
    title: 'Loyalty',
    subtitle: 'What customers can spend their points on, and what each one is worth.',
    searchPlaceholder: 'Search by name…',
    showingOf: 'Showing {{shown}} of {{total}}',
    loadFailed: 'The loyalty rewards could not be loaded.',
    none: 'No loyalty rewards yet.',
    noneMatch: 'No rewards match these filters.',

    status: 'Status',
    statusAny: 'Any status',
    statusLive: 'Live',
    statusScheduled: 'Scheduled',
    statusEnded: 'Ended',
    statusInactive: 'Inactive',

    reward: 'Reward',
    points: 'Points',
    pointsHint: 'What a customer spends to redeem this once.',
    pointsFor: '{{points}} points for {{value}}',
    worth: 'Worth',
    window: 'Window',
    edit: 'Edit',
    noProduct: 'No product attached',

    newReward: 'New reward',
    editReward: 'Edit reward',
    create: 'Create',
    created: 'Reward created.',
    updated: 'Reward saved.',
    saveFailed: 'The reward could not be saved.',
    remove: 'Remove',
    removeConfirm:
      '"{{name}}" stops being offered. Points already spent on it are not returned.',
    removed: 'Reward removed.',

    details: 'Details',
    internalName: 'Internal name',
    internalNameHint: 'How you will recognise this reward in the list.',
    name: 'Customer-facing name',
    nameHint: 'What the customer sees when spending their points.',
    ordering: 'Order',
    orderingHint: 'Lower numbers come first.',
    isActive: 'Active',
    isActiveHint: 'Off hides the reward from customers, whatever its dates say.',

    whatItGives: 'What it gives',
    type: 'Type',
    typeHint: 'The two product rewards need a product attached below.',
    type0: 'Amount off the order',
    type1: 'Percent off the order',
    type2: 'Amount off one product',
    type3: 'Percent off one product',
    amountOff: 'Amount off',
    percentOff: 'Percent off',
    percentHint: 'Entered and shown as a percentage — 10 means 10% off.',
    maxValue: 'Most it can give',
    maxValueHint:
      'A hard ceiling in money. Applied to every type, so it must be at least as much as the reward is worth.',
    maxValueZero:
      'A ceiling of zero means this reward discounts nothing, whatever its value says.',
    givesNothing: 'Gives nothing',
    givesNothingHint:
      'The ceiling is zero, and every redemption is capped to it — so this reward takes points and discounts $0. Set a ceiling to fix it.',
    minOrder: 'Minimum order',
    minOrderHint: 'Leave at 0 for no minimum.',
    product: 'Product',
    productHint: 'The reward applies to this product only.',
    changeProduct: 'Choose a different product',
    searchItems: 'Search the catalogue…',
    noItemsFound: 'No products matched.',

    schedule: 'Schedule',
    starts: 'Starts',
    ends: 'Ends',
    endBeforeStart: 'The end has to come after the start.',
  },

  configuration: {
    title: 'Configuration',
    subtitle:
      'The JSON config files in blob storage. Changes overwrite the whole file when you save.',
    file: 'File',
    file_server: 'Server',
    file_app: 'App',
    file_manager: 'Manager',
    missingShort: 'not found',
    missing: 'There is no {{file}} in blob storage yet. Add a setting and save to create it.',
    search: 'Search',
    searchPlaceholder: 'Filter by key or value…',
    loadFailed: 'This configuration file could not be loaded.',
    saved: 'Configuration saved.',
    saveFailed: 'The configuration could not be saved.',

    settingCount: 'Showing {{shown}} of {{total}} settings',
    empty: 'This file has no settings.',
    addSetting: 'Add setting',
    addBody: 'Added locally. Nothing is written until you save the file.',
    key: 'Key',
    value: 'Value',
    keyExists: 'There is already a setting with that key.',
    willBeStoredAs: 'Will be stored as: {{kind}}.',
    removeSetting: 'Remove setting',
    removeAll: 'Remove all',
    removeAllConfirm_one:
      '{{count}} setting is cleared locally. The file is only emptied once you save.',
    removeAllConfirm_other:
      '{{count}} settings are cleared locally. The file is only emptied once you save.',
    download: 'Download',
    unsaved: 'You have unsaved changes. Nothing is written to the file until you save.',
    invalid_one: '{{count}} setting has a value that does not match its type. Fix it before saving.',
    invalid_other: '{{count}} settings have values that do not match their types. Fix them before saving.',

    kind_string: 'Text',
    kind_number: 'Number',
    kind_boolean: 'True/false',
    kind_json: 'JSON',
    invalid_number: 'This has to be a number.',
    invalid_boolean: 'This has to be true or false.',
    invalid_json: 'This is not valid JSON.',
    invalid_string: '',
  },

  adminPos: {
    title: 'POS Lanes',
    subtitle: 'Every till on the platform, and which build each one is running.',
    searchPlaceholder: 'Search by lane, store, device or terminal…',
    showingOf: 'Showing {{shown}} of {{total}}',
    loadFailed: 'The POS lanes could not be loaded.',
    none: 'No POS lanes registered.',
    noneMatch: 'No lanes match these filters.',

    latestBuild: 'Latest build',
    latestBuildSub: 'The newest any lane has reported.',
    behindSub: 'of {{total}} lanes',
    neverReportedSub_one: '{{count}} has never reported',
    neverReportedSub_other: '{{count}} have never reported',
    totalLanes: 'Lanes',
    build: 'Build',
    anyBuild: 'Any build',
    current: 'Up to date',
    behind: 'Behind',
    unknown: 'Never reported',
    neverReported: 'Never reported',

    store: 'Store',
    allStores: 'All stores',
    lane: 'Lane',
    version: 'Version',
    device: 'Device',
    lastTransaction: 'Last sale',
    access: 'Access',
    anyAccess: 'Any access',
    allowed: 'Allowed',
    blocked: 'Blocked',
  },

  /** Shared by every client-sorted table; see components/ui/sortable-table. */
  table: {
    noData: 'Nothing to show.',
    sortBy: 'Sort by',
    sortAsc: 'Sort ascending',
    sortDesc: 'Sort descending',
  },

  businessAccounts: {
    title: 'Business Accounts',
    subtitle:
      'The payout account behind each store — where its money goes and how the split works.',
    searchPlaceholder: 'Search by store, account holder or email…',
    showingOf: 'Showing {{shown}} of {{total}}',
    loadFailed: 'The business accounts could not be loaded.',
    none: 'No business accounts yet.',
    noneMatch: 'No accounts match these filters.',
    edit: 'Edit',

    status: 'Status',
    anyStatus: 'Any status',
    active: 'Active',
    inactive: 'Inactive',
    isActive: 'Active',
    isActiveHint: 'Off suspends the account without deleting it.',
    payouts: 'Payouts',
    anyPayouts: 'Any payout setting',
    payoutsOn: 'Payouts on',
    payoutsOff: 'Payouts off',
    allowPayouts: 'Allow payouts',
    allowPayoutsHint: 'Off holds the money rather than sending it on.',

    store: 'Store',
    chooseStore: 'Choose a store…',
    accountHolder: 'Account holder',
    bankAccount: 'Bank account',

    newAccount: 'New account',
    editAccount: 'Edit business account',
    create: 'Create',
    created: 'Business account created.',
    updated: 'Business account saved.',
    saveFailed: 'The business account could not be saved.',
    delete: 'Delete',
    deleteConfirm:
      'The payout account for "{{name}}" is removed and that store stops being paid out. Past payouts are kept.',
    deleted: 'Business account deleted.',

    bank: 'Bank details',
    accountHolderName: 'Account holder name',
    accountNumber: 'Account number',
    routingNumber: 'Routing number',
    reveal: 'Show',
    hide: 'Hide',
    hidden: 'hidden, showing the last four digits',
    minimumPayout: 'Minimum payout',
    minimumPayoutHint: 'Nothing is sent until the balance reaches this.',
    processorAccountId: 'Processor account ID',
    processorAccountIdHint: 'The account this store maps to at the payment processor.',
    payPalClientId: 'PayPal client ID',

    entity: 'Who is being paid',
    holderType: 'Holder type',
    company: 'Company',
    individual: 'Individual',
    businessType: 'Merchant category',
    legalName: 'Legal business name',
    ein: 'EIN',
    firstName: 'First name',
    lastName: 'Last name',
    birthDate: 'Date of birth',
    last4Ssn: 'SSN (last 4)',
    last4SsnHint: 'Only the last four digits are stored.',
    accountName: 'Statement name',
    accountNameHint: 'What appears on the customer’s statement.',
    contactEmail: 'Contact email',
    contactPhone: 'Contact phone',

    address: 'Address',
    address1: 'Address line 1',
    address2: 'Address line 2',
    city: 'City',
    state: 'State',
    postalCode: 'Postal code',
    country: 'Country',

    split: 'Fee split',
    splitHint:
      'The share of each order kept back before payout. Stored as entered — check against the processor agreement before changing them.',
    subtotalPct: 'Subtotal %',
    taxesPct: 'Taxes %',
    serviceFeePct: 'Service fee %',
    processorPct: 'Processor %',
  },

  newBusiness: {
    title: 'New store',
    subtitle:
      'Creates the store and hands it to an owner. Its catalogue, staff and hours are set up afterwards.',
    theStore: 'The store',
    ownerEmail: 'Owner email',
    ownerEmailHint: 'The account that will own this store. It has to exist already.',
    emailInvalid: 'That does not look like an email address.',
    name: 'Store name',
    description: 'Description',
    isBeta: 'Beta',
    isBetaHint:
      'Keeps the store out of the public app while it is being set up. Only accounts with beta access can see it.',

    where: 'Where it is',
    address1: 'Address line 1',
    address2: 'Address line 2',
    city: 'City',
    lat: 'Latitude',
    lon: 'Longitude',
    coordinatesHint:
      'Used to work out delivery distance. Leaving both at zero puts the store off the coast of Africa.',

    fulfilment: 'How it sells',
    allowPickup: 'Allow pickup',
    minimumPickup: 'Minimum pickup order',
    allowDelivery: 'Allow delivery',
    minimumDelivery: 'Minimum delivery order',
    hasDeli: 'Has a deli counter',
    acceptsEbt: 'Accepts EBT',
    pricesIncludeTax: 'Prices include tax',
    pricesIncludeTaxHint: 'Shelf prices already have tax in them, so none is added at checkout.',

    create: 'Create store',
    created: '"{{name}}" created.',
    failed: 'The store could not be created.',
  },

  impersonation: {
    title: 'Impersonate a User',
    subtitle: 'Sign in as a customer to see exactly what they see.',
    warningTitle: 'This replaces your session.',
    warningBody:
      'Every request afterwards is made as that customer, including anything you change. Your own session is kept and the bar at the top of the screen puts it back.',

    searchPlaceholder: 'Search by name, email or phone…',
    startBody: 'Find the customer you need to sign in as. Nothing is listed until you search.',
    noResults: 'Nothing matched "{{term}}".',
    deleted: 'Deleted',
    signInAs: 'Sign in as',

    confirmTitle: 'Sign in as this customer?',
    confirmBody:
      'You will be signed in as {{name}} until you stop. Anything you do will be recorded as them.',
    started: 'Signed in as {{name}}.',
    failed: 'Could not sign in as that customer.',

    bannerViewingAs: 'Viewing as {{name}}.',
    bannerSignedInAs: 'Your session as {{name}} is waiting.',
    stop: 'Stop',
  },

  inventory: {
    title: 'Inventory',
    subtitle: 'Stock on hand, item by item.',
    subtitleFor: 'Stock on hand at {{store}}, item by item.',
    chooseStore: 'Stock belongs to a store. Pick one from the switcher above to see its inventory.',
    searchPlaceholder: 'Search by name, brand or UPC…',
    showingOf: 'Showing {{shown}} of {{total}}',
    loadFailed: 'The inventory could not be loaded.',
    noResults: 'No items match this search.',

    division: 'Division',
    anyDivision: 'Any division',
    department: 'Department',
    anyDepartment: 'Any department',
    category: 'Category',
    anyCategory: 'Any category',
    sortBy: 'Sort by',
    sortDefault: 'Name',
    sortLowest: 'Lowest stock first',
    sortHighest: 'Highest stock first',

    product: 'Product',
    where: 'Where',
    onHand: 'on hand',
    cost: 'Cost',
    flags: 'Flags',
    manage: 'Manage',
    item: 'Item',
    itemFailed: 'This item could not be loaded.',
    hidden: 'Hidden',
    unavailable: 'Unavailable',
    untracked: 'Not tracked',

    tabMove: 'Move stock',
    tabSettings: 'Settings',
    tabHistory: 'History',

    movementType: 'What happened',
    type0: 'Sale',
    type1: 'Adjustment up',
    type2: 'Adjustment down',
    type3: 'Receiving',
    amount: 'Amount',
    amountAddHint: 'How many units to add.',
    amountRemoveHint: 'How many units to take away. Enter it as a positive number.',
    reason: 'Reason',
    reasonHint: 'Recorded against the movement, and what the loss reports group by.',
    reason0: 'Not given',
    reason1: 'Damage',
    reason2: 'Lost',
    reason3: 'Found',
    reason4: 'Replenishment',
    reason5: 'Sampling',
    reason6: 'Gift',
    reason7: 'Spoiled',
    reason8: 'Transfer to kitchen',
    reason9: 'Transfer to another store',
    reason10: 'Transfer to deli',
    reason11: 'Layaway',
    changedAt: 'When it happened',
    changedAtHint: 'Leave empty to record it as now.',
    comments: 'Note',
    echo: 'Takes this item from {{from}} to {{to}}.',
    record: 'Record movement',
    movementRecorded: 'Movement recorded.',
    actionFailed: 'That action could not be completed.',

    affectsInventory: 'Track stock',
    affectsInventoryHint: 'Off means sales never change the count, and the number below stops meaning anything.',
    isVisible: 'Visible',
    isVisibleHint: 'Off hides the product from shoppers entirely.',
    isAvailable: 'Available to buy',
    isAvailableHint: 'Off leaves it visible but not orderable. Reverting a movement resets this from the new count.',
    costs: 'Costs',
    costOfBox: 'Cost of box',
    pack: 'Units per box',
    packHint: 'Used to work out a unit cost from a box cost.',
    saveCosts: 'Save costs',
    costsSaved: 'Costs saved.',

    noHistory: 'Nothing has moved on this item yet.',
    revert: 'Revert',
    revertConfirm:
      'Takes {{amount}} back off the count and deletes this movement from the history, so nothing records that it happened. It also resets whether the item is available to buy.',
    reverted: 'Movement reverted.',

    packsAndUnits: '{{packs}} boxes and {{units}} loose',
    recalculate: 'Recalculate',
    recalculated: 'Count rebuilt from the movement history.',
    recalculateUntracked: 'This item does not track stock, so there is nothing to rebuild.',

    helpers: 'Work out the amount',
    boxes: 'Boxes',
    unitsPerBox: 'Units each',
    useIt: 'Use',
    countedTo: 'Counted',
    countedToHint: 'Type what you counted and this sets the direction and the difference.',

    breakdown: 'Break down',
    breakdownBody:
      'Opening a box adds one to that row and takes its units off {{name}}. Boxing up does the reverse.',
    noRelated: 'This item has nothing to break down into.',
    worthUnits_one: 'worth {{count}} unit',
    worthUnits_other: 'worth {{count}} units',
    hasNow: 'has {{quantity}}',
    open: 'Open',
    boxUp: 'Box up',
    breakdownEcho: 'Takes {{name}} from {{from}} to {{to}}.',
    breakdownNegative:
      'This would take {{name}} to {{to}}, which is below zero. Reduce the boxes you are opening.',
    applyBreakdown: 'Apply',
    breakdownDone: 'Breakdown applied.',
    export: 'Export',
    exported: 'Catalogue exported.',
    exportedIgnoringSearch:
      'Catalogue exported. The filters were applied; the search box was not — the export endpoint takes no search term.',
    exportFailed: 'The catalogue could not be exported.',
    sharedStores: 'Shared stores',
    sharedStoresBody: 'Stores whose stock is kept in step with {{store}}.',
    linkStore: 'Link a store',
    chooseStoreToLink: 'Choose a store…',
    link: 'Link',
    unlink: 'Unlink',
    noSharedStores: 'This store shares its inventory with nobody.',
    done: 'Done',
  },

  inventoryReport: {
    title: 'Inventory Report',
    subtitle:
      'What moved in a period, what it was worth, and what is left on the shelf.',
    searchPlaceholder: 'Search by product or GTIN…',
    showingOf: 'Showing {{shown}} of {{total}}',
    loadFailed: 'The inventory report could not be loaded.',
    noResults: 'Nothing moved in this period for these filters.',

    store: 'Store',
    allStores: 'All stores',
    movementType: 'Movement',
    anyType: 'Any movement',
    reasons: 'Reasons',
    anyReason: 'Any reason',
    nReasons: '{{count}} reasons',
    division: 'Division',
    anyDivision: 'Any division',
    from: 'From',
    to: 'To',

    sortBy: 'Sort by',
    sortDefault: 'Product',
    sortMovedDesc: 'Most moved',
    sortMovedAsc: 'Least moved',
    sortValueDesc: 'Highest value moved',
    sortValueAsc: 'Lowest value moved',
    sortQohDesc: 'Most on hand',
    sortQohAsc: 'Least on hand',

    product: 'Product',
    gtin: 'GTIN',
    moved: 'Moved',
    movedValue: 'Value moved',
    onHand: 'On hand',
    onHandValue: 'Value on hand',
    unitsMoved: '{{amount}} units',
    onHandRow: '{{quantity}} on hand, worth {{value}}',
    onHandNow:
      'Across everything matching these filters: {{quantity}} units on hand, worth {{value}}.',

    export: 'Export',
    exported_one: '{{count}} row exported.',
    exported_other: '{{count}} rows exported.',
    nothingToExport: 'Nothing to export for these filters.',
    exportFailed: 'The report could not be exported.',
  },

  filters: {
    title: 'Filters',
    apply: 'Apply',
    reset: 'Reset',
  },

  nav: {
    sections: {
      platform: 'Platform',
      booking: 'Booking',
      finance: 'Finance',
      catalog: 'Catalog',
      marketing: 'Marketing',
      store: 'Store',
      support: 'Support',
      general: 'General',
    },

    home: 'Home',
    globalDashboard: 'Global Dashboard',
    storeDashboard: 'Store Dashboard',
    globalPayouts: 'Global Payouts',
    reports: 'Reports',
    logsGlobal: 'Global Logs',
    documents: 'Documents',
    downloads: 'Downloads',
    checkHealth: 'Check Health',

    adminServices: 'Admin Services',
    businessAccounts: 'Business Accounts',
    newBusiness: 'New Business',
    documentAcceptance: 'Document Acceptance',
    impersonation: 'Impersonate User',
    coupons: 'Coupons',
    giftCards: 'Gift Cards',
    cardRestrictions: 'Card Restrictions',
    deliveryProviders: 'Delivery Providers',
    adminPosLanes: 'POS Lanes',
    imagesService: 'Images Service',
    changeApiUrl: 'Change API URL',
    configuration: 'Configuration',
    configServer: 'Server',
    configApp: 'App',
    configManager: 'Manager',

    customerService: 'Customer Service',
    ordersGlobal: 'Orders',
    users: 'Users',
    accountRequests: 'Account Requests',
    referralEmail: 'Referral Email',
    externalContacts: 'External Contacts',
    layawaysGlobal: 'All Layaways',
    paypalInvoicesGlobal: 'PayPal Invoices',

    analytics: 'Analytics',
    usageAnalytics: 'App Usage',

    booking: {
      maintenance: 'Maintenance',
      management: 'Management',
      reports: 'Reports',

      membershipPlans: 'Membership Plans',
      events: 'Events',
      membershipGifts: 'Membership Gifts',
      giftVouchers: 'Gift Vouchers',
      giftVouchersManage: 'Gift Vouchers',
      giftVouchersReport: 'Gift Vouchers',

      manageBookings: 'Bookings',
      manageSubscriptions: 'Subscriptions',
      paymentReview: 'Payment Review',

      businessOverview: 'Business Overview',
      reportsBookings: 'Bookings',
      reportsSubscriptions: 'Subscriptions',
      sales: 'Sales',
      paymentHistory: 'Payment History',
      timeslotHosts: 'Timeslot Hosts',
      heatmaps: 'Heatmaps',
      heatmapSales: 'Sales',
      heatmapBookings: 'Bookings',

      hosts: 'Hosts',
      locations: 'Locations',
      serviceTypes: 'Service Types',
    },

    finance: {
      sales: 'Sales',
      salesReport: 'Sales report',
      general: 'General',
      inStore: 'In Store',
      inStoreEmployees: 'In-Store Employees',
      settlements: 'Settlements',
      byStation: 'By Station',
      summary: 'Summary',

      movement: 'Movement',
      division: 'Division',
      group: 'Group',
      department: 'Department',
      category: 'Category',

      loss: 'Loss',
      targetVsActual: 'Target vs Actual',
      participation: 'Participation',
      monthly: 'Monthly',

      posDiscountReport: 'POS Discount Report',
      inventoryReport: 'Inventory Report',
      invoices: 'Invoices',
      topProducts: 'Top Products',
    },

    catalog: {
      title: 'Catalog',
      itemMaster: 'Item Master',
      itemRegistration: 'Item Registration',
      departments: 'Departments',
      categories: 'Categories',
      searchOptimization: 'Search Optimization',
      consumerSearch: 'Consumer Search',
      itemPrices: 'Item Prices',
      requirements: 'Requirements',
      sides: 'Sides',
      addons: 'Add-ons',
    },

    marketing: {
      title: 'Marketing',
      promotions: 'Promotions',
      promoCodes: 'Promo Codes',
      bannerAds: 'Banner Ads',
      promoEmails: 'Promo Emails',
      pushNotifications: 'Push Notifications',
      productCampaign: 'Product Campaigns',
      infoCards: 'Info Cards',
      sponsorships: 'Sponsorships',
      posDiscounts: 'POS Discounts',
      loyalty: 'Loyalty',
    },

    store: {
      title: 'Store',
      orders: 'Store Orders',
      customers: 'Customers',
      storage: 'Storage',
      onBehalf: 'On Behalf',
      products: 'Products',
      storeItemPrices: 'Store Item Prices',
      shopper: 'Shopper',
      storeCoupons: 'Store Coupons',
      selfCheckout: 'Self Checkout',
      inventory: 'Inventory',
      performance: 'Performance',
      sorting: 'Sorting',
      chats: 'Chats',
      logs: 'Store Logs',
      paypalInvoices: 'PayPal Invoices',

      staff: 'Staff',
      staffList: 'Team',
      timesheets: 'Timesheets',
      timesheetTypes: 'Timesheet Types',

      pos: 'POS',
      posLanes: 'Lanes',
      cashDrawerReport: 'Cash Drawer Report',

      layaway: 'Layaway',
      bins: 'Bins',
      layaways: 'Layaways',

      deliveries: 'Deliveries',

      payments: 'Payments',
      payouts: 'Payouts',
      paymentOptions: 'Options',

      settings: 'Settings',
      general: 'General',
      options: 'Options',
      hours: 'Hours',
      delivery: 'Delivery',
      customDepartments: 'Custom Departments',
      storeDocuments: 'Store Documents',
    },
  },

  pages: {
    home: {
      welcome: 'Welcome back, {{name}}',
      scopedTo: 'Viewing {{store}}.',
      global: 'Viewing all stores.',
      shortcuts: 'Jump to',
      frequent: 'Your most visited',
      frequentHelp: 'Where you have gone most in the last 30 days.',
      more: 'More',
      goodToKnow: 'Good to know',
      tipStoreTitle: 'Switching stores',
      tipStoreBodyGlobal:
        'Pick a store from the switcher in the sidebar to scope pages to it. Store-only pages appear once one is selected.',
      tipStoreBodyScoped:
        'Pages are scoped to {{store}}. Switch back to Global from the sidebar to see every store at once.',
      tipSearchTitle: 'Find any page',
      tipSearchBody: 'Press ⌘K (Ctrl+K on Windows) to search pages and jump straight to them.',
      tipPrefsTitle: 'Language and theme',
      tipPrefsBody: 'Both are under your avatar menu in the top right.',
    },
    placeholder: {
      description: 'This page has not been built in the redesign yet.',
      title: 'Not implemented yet',
      body: 'The navigation entry is wired up — the page itself is still to come.',
      fallbackName: 'Page',
    },
    notFound: {
      title: 'Page not found',
      body: 'That URL does not match anything in the portal.',
    },
    unauthorized: {
      title: 'You do not have access',
      body: 'Your account is missing the role required for this page. Ask an administrator if you think this is wrong.',
    },
  },
} as const;

/**
 * The same key structure with the values widened to `string` — `as const` above
 * would otherwise pin each value to its own English literal type, and every
 * Spanish string would fail to assign.
 */
type DeepString<T> = {
  [K in keyof T]: T[K] extends string ? string : DeepString<T[K]>;
};

export type Translations = DeepString<typeof en>;
